import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { AuscultationPlayer, SoundLibrary, LUNG_SOUNDS, BOWEL_SOUNDS, HEART_SOUNDS } from '../components/AuscultationAudio';
import { 
  Stethoscope, Activity, FileText, ClipboardCheck, ArrowLeft, Heart, Plus, X,
  Brain, Pill, TestTube, AlertTriangle, CheckCircle2, XCircle, Award,
  TrendingUp, Shield, MessageSquare, BookOpen, Clock, Target, Phone, Send,
  User, CheckCircle, Volume2
} from 'lucide-react';

// ============================================
// NCLEX MED-SURG CONDITIONS DATABASE
// ============================================
const MEDSURG_CONDITIONS = {
  "Cardiovascular": {
    "Congestive Heart Failure (CHF)": {
      pathophysiology: "Heart's inability to pump sufficient blood to meet body's metabolic demands. Left-sided failure leads to pulmonary congestion; right-sided failure leads to systemic venous congestion.",
      etiology: ["Coronary artery disease", "Hypertension", "Cardiomyopathy", "Valvular disease", "Arrhythmias"],
      clinicalManifestations: ["Dyspnea on exertion", "Orthopnea", "Paroxysmal nocturnal dyspnea", "Peripheral edema", "Jugular vein distension", "S3 heart sound", "Crackles in lungs"],
      diagnostics: ["BNP >100 pg/mL", "Chest X-ray: cardiomegaly, pulmonary congestion", "Echocardiogram: EF <40%", "ECG: arrhythmias, LVH"],
      nursingInterventions: ["Monitor I&O", "Daily weights", "Elevate HOB 45°", "Oxygen therapy", "Sodium restriction", "Assess breath sounds Q4H"],
      medications: ["ACE inhibitors (Lisinopril)", "Beta-blockers (Carvedilol)", "Diuretics (Furosemide)", "Digoxin", "Aldosterone antagonists (Spironolactone)"]
    },
    "Acute Myocardial Infarction (MI)": {
      pathophysiology: "Complete occlusion of coronary artery leading to myocardial ischemia and necrosis. Typically caused by plaque rupture and thrombus formation.",
      etiology: ["Atherosclerosis", "Coronary artery spasm", "Thrombosis", "Cocaine use"],
      clinicalManifestations: ["Crushing substernal chest pain >20 min", "Radiation to left arm/jaw/back", "Diaphoresis", "Nausea/vomiting", "Dyspnea", "Anxiety", "Denial"],
      diagnostics: ["Troponin I/T elevated", "CK-MB elevated", "ECG: ST elevation (STEMI) or depression (NSTEMI)", "Cardiac catheterization"],
      nursingInterventions: ["MONA protocol", "Continuous cardiac monitoring", "12-lead ECG", "IV access", "Bed rest", "Anxiety reduction", "Pain assessment"],
      medications: ["Morphine", "Oxygen", "Nitrates", "Aspirin", "Anticoagulants (Heparin)", "Thrombolytics", "Beta-blockers"]
    },
    "Hypertension": {
      pathophysiology: "Sustained elevation of arterial blood pressure (>130/80 mmHg). Increases cardiac workload and damages blood vessels over time.",
      etiology: ["Essential (primary): unknown", "Secondary: renal disease, endocrine disorders, medications"],
      clinicalManifestations: ["Often asymptomatic (silent killer)", "Headache", "Dizziness", "Visual changes", "Epistaxis in severe cases"],
      diagnostics: ["BP >130/80 on multiple occasions", "BMP for renal function", "Lipid panel", "ECG for LVH", "Urinalysis"],
      nursingInterventions: ["Monitor BP accurately", "DASH diet education", "Medication compliance", "Lifestyle modifications", "Stress management"],
      medications: ["ACE inhibitors", "ARBs", "Calcium channel blockers", "Thiazide diuretics", "Beta-blockers"]
    },
    "Deep Vein Thrombosis (DVT)": {
      pathophysiology: "Blood clot formation in deep veins, usually in lower extremities. Risk of pulmonary embolism if clot dislodges.",
      etiology: ["Virchow's triad: stasis, hypercoagulability, endothelial injury", "Immobility", "Surgery", "Malignancy", "Oral contraceptives"],
      clinicalManifestations: ["Unilateral leg swelling", "Pain/tenderness", "Warmth", "Erythema", "Positive Homans' sign (unreliable)"],
      diagnostics: ["D-dimer elevated", "Doppler ultrasound", "Venography", "CT venography"],
      nursingInterventions: ["Bed rest initially", "Anticoagulation therapy", "Leg elevation", "SCDs/TED stockings", "No massage of affected leg", "Monitor for PE signs"],
      medications: ["Heparin (initial)", "Warfarin (maintenance)", "DOACs (Rivaroxaban, Apixaban)", "Thrombolytics if severe"]
    }
  },
  "Respiratory": {
    "Chronic Obstructive Pulmonary Disease (COPD)": {
      pathophysiology: "Progressive airflow limitation due to chronic bronchitis (mucus hypersecretion) and emphysema (alveolar destruction). Irreversible airway obstruction.",
      etiology: ["Smoking (primary)", "Alpha-1 antitrypsin deficiency", "Occupational exposure", "Air pollution"],
      clinicalManifestations: ["Chronic productive cough", "Dyspnea on exertion", "Barrel chest", "Pursed-lip breathing", "Use of accessory muscles", "Wheezing"],
      diagnostics: ["Spirometry: FEV1/FVC <70%", "ABGs: hypoxemia, hypercapnia", "Chest X-ray: hyperinflation", "Alpha-1 antitrypsin level"],
      nursingInterventions: ["Low-flow O2 (1-2 L/min)", "Pursed-lip breathing", "Smoking cessation", "Pulmonary rehabilitation", "Position for comfort", "Monitor for respiratory failure"],
      medications: ["Short-acting bronchodilators (Albuterol)", "Long-acting bronchodilators (Tiotropium)", "Inhaled corticosteroids", "Systemic steroids for exacerbations", "Antibiotics if infection"]
    },
    "Pneumonia": {
      pathophysiology: "Infection causing inflammation of alveoli and interstitial lung tissue. Leads to consolidation and impaired gas exchange.",
      etiology: ["Bacterial (S. pneumoniae most common)", "Viral", "Aspiration", "Hospital-acquired (HAP/VAP)"],
      clinicalManifestations: ["Fever/chills", "Productive cough", "Pleuritic chest pain", "Tachypnea", "Crackles/bronchial breath sounds", "Dullness to percussion"],
      diagnostics: ["Chest X-ray: infiltrates", "CBC: elevated WBC", "Sputum culture", "Blood cultures", "Procalcitonin"],
      nursingInterventions: ["Oxygen therapy", "Incentive spirometry", "HOB elevation", "Adequate hydration", "Isolation if appropriate", "Monitor respiratory status"],
      medications: ["Antibiotics (based on culture)", "Antipyretics", "Bronchodilators", "Mucolytics"]
    },
    "Asthma": {
      pathophysiology: "Chronic inflammatory airway disease with reversible bronchoconstriction, airway inflammation, and mucus production. Triggered by various stimuli.",
      etiology: ["Allergens", "Exercise", "Cold air", "Infections", "Stress", "NSAIDs", "Genetic predisposition"],
      clinicalManifestations: ["Wheezing", "Dyspnea", "Chest tightness", "Cough (especially at night)", "Prolonged expiratory phase", "Use of accessory muscles"],
      diagnostics: ["Spirometry: reversible obstruction (>12% improvement post-bronchodilator)", "Peak flow monitoring", "Allergy testing", "Chest X-ray usually normal"],
      nursingInterventions: ["Administer bronchodilators", "Position upright", "Calm environment", "Identify triggers", "Action plan education", "Monitor peak flow"],
      medications: ["Quick relief: SABA (Albuterol)", "Long-term: ICS (Fluticasone)", "LABA (Salmeterol)", "Leukotriene modifiers (Montelukast)", "Systemic steroids for severe"]
    },
    "Pulmonary Embolism (PE)": {
      pathophysiology: "Obstruction of pulmonary artery by thrombus (usually from DVT), fat, air, or amniotic fluid. Causes V/Q mismatch and hypoxemia.",
      etiology: ["DVT (most common)", "Immobility", "Surgery", "Malignancy", "Hypercoagulable states", "Fractures"],
      clinicalManifestations: ["Sudden dyspnea", "Pleuritic chest pain", "Tachycardia", "Tachypnea", "Hemoptysis", "Anxiety", "Hypotension if massive"],
      diagnostics: ["D-dimer", "CT pulmonary angiography (gold standard)", "V/Q scan", "ABGs: hypoxemia, respiratory alkalosis", "ECG: S1Q3T3 pattern"],
      nursingInterventions: ["Oxygen therapy", "IV access", "Continuous monitoring", "Anticoagulation", "Emotional support", "Prevent further emboli", "Prepare for thrombolytics if unstable"],
      medications: ["Heparin", "Warfarin/DOACs", "Thrombolytics (tPA) if massive", "Pain management"]
    }
  },
  "Endocrine": {
    "Diabetes Mellitus Type 2": {
      pathophysiology: "Progressive insulin resistance and relative insulin deficiency leading to hyperglycemia. Beta cell dysfunction worsens over time.",
      etiology: ["Obesity", "Sedentary lifestyle", "Genetic predisposition", "Age >45", "Metabolic syndrome"],
      clinicalManifestations: ["Polyuria", "Polydipsia", "Polyphagia", "Fatigue", "Blurred vision", "Slow wound healing", "Recurrent infections"],
      diagnostics: ["FBG ≥126 mg/dL", "HbA1c ≥6.5%", "Random glucose ≥200 with symptoms", "OGTT ≥200 at 2hr"],
      nursingInterventions: ["Blood glucose monitoring", "Diet education", "Exercise promotion", "Foot care", "Medication administration", "Hypoglycemia recognition", "Sick day management"],
      medications: ["Metformin (first-line)", "Sulfonylureas", "GLP-1 agonists", "SGLT2 inhibitors", "DPP-4 inhibitors", "Insulin if needed"]
    },
    "Diabetic Ketoacidosis (DKA)": {
      pathophysiology: "Life-threatening complication of diabetes with severe insulin deficiency leading to hyperglycemia, ketosis, and metabolic acidosis.",
      etiology: ["Infection (most common trigger)", "Missed insulin doses", "New-onset Type 1 DM", "Stress", "MI"],
      clinicalManifestations: ["Kussmaul respirations", "Fruity breath odor", "Nausea/vomiting", "Abdominal pain", "Altered mental status", "Dehydration", "Polyuria"],
      diagnostics: ["Glucose >250 mg/dL", "pH <7.3", "Serum ketones positive", "Anion gap >12", "Bicarbonate <18"],
      nursingInterventions: ["IV fluid resuscitation", "Insulin drip", "Electrolyte monitoring (especially K+)", "Frequent glucose checks", "Identify precipitating factor", "Neurologic checks"],
      medications: ["Regular insulin IV", "Normal saline then D5 1/2NS", "Potassium replacement", "Bicarbonate if pH <6.9"]
    },
    "Hypothyroidism": {
      pathophysiology: "Insufficient thyroid hormone production leading to decreased metabolic rate. Most commonly caused by Hashimoto's thyroiditis.",
      etiology: ["Hashimoto's thyroiditis", "Thyroidectomy", "Radioactive iodine", "Medications (lithium, amiodarone)", "Iodine deficiency"],
      clinicalManifestations: ["Fatigue", "Weight gain", "Cold intolerance", "Constipation", "Dry skin", "Bradycardia", "Depression", "Myxedema"],
      diagnostics: ["TSH elevated", "Free T4 decreased", "Anti-TPO antibodies (Hashimoto's)"],
      nursingInterventions: ["Administer thyroid hormone", "Monitor for myxedema coma", "Prevent constipation", "Safety precautions (drowsiness)", "Monitor cardiac status"],
      medications: ["Levothyroxine (Synthroid)", "Take on empty stomach", "Monitor TSH levels"]
    }
  },
  "Renal": {
    "Acute Kidney Injury (AKI)": {
      pathophysiology: "Sudden decline in kidney function with accumulation of waste products. Can be prerenal, intrarenal, or postrenal.",
      etiology: ["Prerenal: hypovolemia, heart failure, sepsis", "Intrarenal: ATN, nephrotoxins, glomerulonephritis", "Postrenal: obstruction"],
      clinicalManifestations: ["Oliguria (<400 mL/day)", "Elevated BUN/Cr", "Fluid overload", "Electrolyte imbalances", "Uremia symptoms"],
      diagnostics: ["Serum Cr increase >0.3 mg/dL in 48hr", "BUN elevated", "Urinalysis: casts", "Renal ultrasound", "FENa"],
      nursingInterventions: ["Strict I&O", "Daily weights", "Fluid restriction if oliguric", "Avoid nephrotoxins", "Monitor electrolytes", "Dialysis preparation"],
      medications: ["Loop diuretics (if fluid overload)", "Avoid NSAIDs/contrast", "Adjust renally excreted drugs", "Treat underlying cause"]
    },
    "Chronic Kidney Disease (CKD)": {
      pathophysiology: "Progressive, irreversible loss of kidney function over months to years. Leads to decreased GFR and accumulation of toxins.",
      etiology: ["Diabetes (leading cause)", "Hypertension", "Glomerulonephritis", "Polycystic kidney disease"],
      clinicalManifestations: ["Fatigue", "Edema", "Hypertension", "Anemia", "Bone disease", "Uremic symptoms (nausea, pruritus, confusion)"],
      diagnostics: ["GFR <60 for 3+ months", "Elevated Cr/BUN", "Proteinuria", "Anemia", "Metabolic acidosis", "Hyperkalemia"],
      nursingInterventions: ["BP control", "Protein/phosphorus restriction", "Fluid management", "Anemia management", "Dialysis education", "Transplant evaluation"],
      medications: ["ACE inhibitors/ARBs", "Phosphate binders", "EPO for anemia", "Vitamin D supplements", "Loop diuretics"]
    }
  },
  "Gastrointestinal": {
    "GI Bleeding": {
      pathophysiology: "Blood loss from the GI tract. Upper GI bleeding (above ligament of Treitz) often from ulcers, varices. Lower GI from diverticulosis, cancer.",
      etiology: ["Upper: PUD, esophageal varices, Mallory-Weiss tear", "Lower: diverticulosis, hemorrhoids, colorectal cancer"],
      clinicalManifestations: ["Upper: hematemesis, melena, coffee-ground emesis", "Lower: hematochezia, maroon stools", "Signs of hypovolemia"],
      diagnostics: ["CBC: decreased H&H", "BUN:Cr ratio >20:1 (upper)", "Endoscopy/colonoscopy", "Type & screen", "Coagulation studies"],
      nursingInterventions: ["Assess hemodynamic status", "IV access (2 large bore)", "NPO", "NG tube if indicated", "Blood transfusion preparation", "Monitor vitals Q15min"],
      medications: ["PPI (Pantoprazole)", "Octreotide (for varices)", "Blood products", "Vasopressors if needed"]
    },
    "Liver Cirrhosis": {
      pathophysiology: "End-stage liver disease with irreversible scarring and nodule formation. Leads to portal hypertension and liver failure.",
      etiology: ["Alcohol abuse (most common in US)", "Hepatitis B/C", "NAFLD", "Autoimmune hepatitis", "Biliary disease"],
      clinicalManifestations: ["Jaundice", "Ascites", "Hepatic encephalopathy", "Varices", "Coagulopathy", "Spider angiomas", "Palmar erythema"],
      diagnostics: ["Elevated liver enzymes", "Decreased albumin", "Elevated PT/INR", "Thrombocytopenia", "Liver biopsy", "Ultrasound/CT"],
      nursingInterventions: ["Monitor mental status", "Measure abdominal girth", "Low sodium diet", "Protein restriction if encephalopathy", "Fall precautions", "Bleeding precautions"],
      medications: ["Lactulose (for encephalopathy)", "Rifaximin", "Diuretics (Spironolactone, Furosemide)", "Beta-blockers (variceal prevention)", "Vitamin K"]
    },
    "Cholecystitis": {
      pathophysiology: "Inflammation of the gallbladder, usually caused by gallstones blocking the cystic duct. Leads to bile stasis, inflammation, and potential infection.",
      etiology: ["Gallstones (90%)", "Biliary sludge", "Acalculous cholecystitis (critically ill)", "Bile duct obstruction"],
      clinicalManifestations: ["Right upper quadrant pain", "Murphy's sign positive", "Nausea/vomiting", "Fever", "Referred pain to right scapula", "Guarding"],
      diagnostics: ["RUQ ultrasound (gold standard)", "HIDA scan", "Elevated WBC", "Elevated liver enzymes/bilirubin", "CT if complications suspected"],
      nursingInterventions: ["NPO", "IV fluids", "Pain management", "Nasogastric tube if severe vomiting", "Monitor for complications (perforation, gangrene)", "Pre-op preparation"],
      medications: ["IV antibiotics", "Analgesics (avoid morphine - may cause sphincter of Oddi spasm)", "Antiemetics", "PPI"]
    }
  },
  "Neurological": {
    "Stroke (CVA)": {
      pathophysiology: "Interruption of blood flow to brain. Ischemic (87%): thrombosis/embolism. Hemorrhagic: intracerebral/subarachnoid bleeding.",
      etiology: ["Ischemic: atherosclerosis, AFib, carotid stenosis", "Hemorrhagic: hypertension, aneurysm, AVM"],
      clinicalManifestations: ["Sudden focal neurological deficits", "Hemiparesis/hemiplegia", "Facial droop", "Speech difficulties", "Vision changes", "Severe headache (hemorrhagic)"],
      diagnostics: ["CT without contrast (rule out hemorrhage)", "MRI", "CT angiography", "Carotid ultrasound", "Echocardiogram"],
      nursingInterventions: ["Time of symptom onset", "NIH Stroke Scale", "Thrombolytic eligibility", "Swallow evaluation before PO", "Neuro checks Q1-2H", "BP management", "DVT prevention"],
      medications: ["tPA if within window (ischemic)", "Aspirin", "Antihypertensives (carefully)", "Anticoagulants (if AFib)", "Statins"]
    },
    "Seizures": {
      pathophysiology: "Abnormal, excessive electrical discharge in the brain. Can be partial (focal) or generalized. Status epilepticus is a medical emergency.",
      etiology: ["Epilepsy", "Stroke", "Brain tumor", "Infection", "Metabolic disturbances", "Drug/alcohol withdrawal", "Head trauma"],
      clinicalManifestations: ["Tonic-clonic movements", "Loss of consciousness", "Incontinence", "Post-ictal confusion", "Aura (warning signs)", "Automatisms"],
      diagnostics: ["EEG", "MRI brain", "CT head", "Metabolic panel", "Drug levels", "Lumbar puncture if infection suspected"],
      nursingInterventions: ["Protect from injury", "Do NOT restrain", "Do NOT put anything in mouth", "Turn to side", "Time the seizure", "Suction available", "O2 after seizure"],
      medications: ["Benzodiazepines (acute)", "Phenytoin/Fosphenytoin", "Levetiracetam", "Valproic acid", "Carbamazepine"]
    },
    "Traumatic Brain Injury": {
      pathophysiology: "Injury to brain tissue from external force. Can cause contusions, hemorrhage (epidural, subdural, subarachnoid), diffuse axonal injury. Secondary injury from edema and increased ICP.",
      etiology: ["Falls", "Motor vehicle accidents", "Assault", "Sports injuries", "Blast injuries"],
      clinicalManifestations: ["Altered LOC (GCS)", "Pupil changes", "Motor deficits", "Cushing's triad (late: HTN, bradycardia, irregular respirations)", "CSF leak", "Battle's sign", "Raccoon eyes"],
      diagnostics: ["CT head without contrast (initial)", "MRI for subtle injuries", "ICP monitoring", "GCS assessment", "Neuro checks"],
      nursingInterventions: ["Maintain airway/C-spine precautions", "Neuro checks Q1H", "HOB 30 degrees", "ICP monitoring", "Prevent secondary injury", "Seizure precautions", "Monitor for diabetes insipidus"],
      medications: ["Osmotic diuretics (Mannitol)", "Hypertonic saline", "Sedation (Propofol)", "Antiseizure prophylaxis (Levetiracetam)", "Avoid hypotension"]
    }
  },
  "Pediatric": {
    "Bronchiolitis": {
      pathophysiology: "Viral infection causing inflammation of the bronchioles, typically in infants <2 years. RSV is the most common cause. Leads to airway obstruction from mucus and edema.",
      etiology: ["RSV (most common)", "Rhinovirus", "Influenza", "Parainfluenza", "Adenovirus"],
      clinicalManifestations: ["Tachypnea", "Wheezing", "Crackles", "Nasal flaring", "Retractions", "Cough", "Poor feeding", "Fever", "Cyanosis in severe cases"],
      diagnostics: ["Clinical diagnosis", "RSV rapid antigen test", "Chest X-ray (hyperinflation, atelectasis)", "Pulse oximetry"],
      nursingInterventions: ["Supportive care", "Maintain hydration", "Nasal suctioning", "Oxygen therapy if SpO2 <90%", "Monitor respiratory status", "Contact isolation for RSV", "Parent education"],
      medications: ["Supportive only (no routine bronchodilators)", "Oxygen", "IV fluids if needed", "Palivizumab (prevention in high-risk)"]
    },
    "Febrile Seizure": {
      pathophysiology: "Seizure occurring with fever in children 6 months to 5 years without CNS infection. Usually benign and self-limited. Simple (< 15 min) vs Complex (>15 min or focal).",
      etiology: ["Rapid rise in temperature", "Viral infections (most common)", "Genetic predisposition", "Post-immunization fever"],
      clinicalManifestations: ["Generalized tonic-clonic movements", "Loss of consciousness", "Post-ictal drowsiness", "Associated with fever >100.4°F (38°C)"],
      diagnostics: ["Clinical diagnosis", "Identify fever source", "LP if meningitis suspected", "Labs to identify infection"],
      nursingInterventions: ["Protect from injury during seizure", "Position on side", "Time seizure duration", "Fever management", "Reassure parents", "Monitor for recurrence"],
      medications: ["Antipyretics (Acetaminophen, Ibuprofen)", "Benzodiazepines if prolonged (Diazepam rectal)", "Treat underlying infection"]
    }
  },
  "Infection/Sepsis": {
    "Sepsis/Septic Shock": {
      pathophysiology: "Life-threatening organ dysfunction caused by dysregulated host response to infection. Progresses from SIRS to sepsis to septic shock.",
      etiology: ["Bacterial infection (most common)", "UTI, pneumonia, wound infection, abdominal infection"],
      clinicalManifestations: ["Fever or hypothermia", "Tachycardia", "Tachypnea", "Altered mental status", "Hypotension (shock)", "Elevated lactate", "Oliguria"],
      diagnostics: ["Blood cultures (before antibiotics)", "Lactate >2 mmol/L", "Procalcitonin", "CBC: elevated WBC or leukopenia", "CMP for organ function"],
      nursingInterventions: ["Early recognition (qSOFA)", "Obtain cultures", "Initiate antibiotics within 1 hour", "Fluid resuscitation (30 mL/kg)", "Vasopressors if needed", "Monitor lactate clearance"],
      medications: ["Broad-spectrum antibiotics", "IV fluids (crystalloids)", "Vasopressors (Norepinephrine)", "Corticosteroids if refractory shock"]
    }
  },
  "Post-Operative": {
    "Post-Operative Care": {
      pathophysiology: "Recovery period after surgery. Risk for complications varies based on surgery type, patient factors, and anesthesia.",
      etiology: ["Planned surgical procedure", "Emergency surgery"],
      clinicalManifestations: ["Pain", "Nausea/vomiting", "Altered LOC (anesthesia recovery)", "Potential complications: bleeding, infection, DVT, ileus"],
      diagnostics: ["Post-op labs (H&H, BMP)", "Imaging as indicated", "Wound assessment"],
      nursingInterventions: ["Airway management", "Pain assessment (0-10 scale)", "Vital signs Q15min initially", "I&O monitoring", "Early ambulation", "Incentive spirometry", "DVT prophylaxis", "Wound care"],
      medications: ["Analgesics (opioids initially, transition to PO)", "Antiemetics", "DVT prophylaxis (Heparin/Enoxaparin)", "Antibiotics if indicated"]
    }
  },
  "Musculoskeletal": {
    "Hip Fracture": {
      pathophysiology: "Break in the proximal femur, commonly at femoral neck or intertrochanteric region. Often results from falls in elderly with osteoporosis.",
      etiology: ["Falls", "Osteoporosis", "Trauma", "Pathologic fractures from metastatic disease"],
      clinicalManifestations: ["Severe hip pain", "Shortened and externally rotated leg", "Inability to bear weight", "Ecchymosis", "Swelling"],
      diagnostics: ["X-ray of hip", "CT if occult fracture suspected", "CBC, BMP", "Type & Screen", "ECG", "Chest X-ray"],
      nursingInterventions: ["Pain management", "Buck's traction pre-op if ordered", "Skin assessment", "Neurovascular checks", "DVT prophylaxis", "Early mobilization post-op", "Fall prevention"],
      medications: ["Analgesics (opioids)", "DVT prophylaxis (Enoxaparin)", "Antibiotics perioperative", "Calcium/Vitamin D", "Bisphosphonates"]
    },
    "Lumbar Radiculopathy": {
      pathophysiology: "Nerve root compression in the lumbar spine, often from herniated disc or spinal stenosis. Causes radiating pain, numbness, and weakness in dermatomal distribution.",
      etiology: ["Herniated disc (most common L4-L5, L5-S1)", "Spinal stenosis", "Degenerative disc disease", "Spondylolisthesis", "Trauma", "Tumor", "Infection"],
      clinicalManifestations: ["Low back pain radiating to leg (sciatica)", "Numbness/tingling in leg/foot", "Muscle weakness", "Decreased reflexes", "Positive straight leg raise", "Pain worse with sitting/bending"],
      diagnostics: ["MRI spine (gold standard)", "X-ray for alignment", "CT myelogram if MRI contraindicated", "EMG/nerve conduction studies", "Neurological exam"],
      nursingInterventions: ["Pain management", "Positioning (semi-Fowler's, pillow under knees)", "Activity modification", "Heat/ice therapy", "Body mechanics education", "Post-operative care if surgical", "Neurovascular checks"],
      medications: ["NSAIDs", "Muscle relaxants (Cyclobenzaprine)", "Gabapentin/Pregabalin for nerve pain", "Oral steroids (short course)", "Epidural steroid injections", "Opioids (short-term)"]
    },
    "Spinal Cord Injury": {
      pathophysiology: "Damage to spinal cord resulting in loss of motor/sensory function below the level of injury. Complete injury = total loss; Incomplete = partial preservation.",
      etiology: ["Trauma (MVA, falls, violence)", "Tumors", "Infections (abscess)", "Vascular (infarction)", "Degenerative conditions"],
      clinicalManifestations: ["Motor/sensory loss below injury level", "Respiratory compromise (high cervical)", "Neurogenic shock (bradycardia, hypotension)", "Spinal shock (areflexia)", "Bladder/bowel dysfunction", "Autonomic dysreflexia"],
      diagnostics: ["CT spine", "MRI spine", "X-ray for alignment", "ASIA impairment scale", "Neurological exam", "Pulmonary function tests"],
      nursingInterventions: ["Spinal immobilization", "Airway management", "Hemodynamic monitoring", "Skin integrity (pressure ulcer prevention)", "Bladder management (I&O, catheterization)", "Bowel program", "DVT prophylaxis", "Psychological support"],
      medications: ["Methylprednisolone (controversial, per protocol)", "DVT prophylaxis", "Bowel regimen", "Baclofen for spasticity", "Anticholinergics for bladder", "Pain management"]
    },
    "Fractures (General)": {
      pathophysiology: "Break in bone continuity from trauma or pathologic process. Healing occurs through inflammation, soft callus, hard callus, and remodeling phases.",
      etiology: ["Trauma", "Osteoporosis", "Pathologic (cancer, infection)", "Stress fractures", "Falls"],
      clinicalManifestations: ["Pain at site", "Swelling", "Deformity", "Crepitus", "Loss of function", "Ecchymosis", "Neurovascular compromise possible"],
      diagnostics: ["X-ray (minimum 2 views)", "CT for complex fractures", "MRI for occult/stress fractures", "Neurovascular assessment", "Compartment pressure if suspected"],
      nursingInterventions: ["Immobilization", "RICE (Rest, Ice, Compression, Elevation)", "Neurovascular checks (5 Ps)", "Pain management", "Cast/splint care", "Weight-bearing status education", "DVT prophylaxis", "Compartment syndrome monitoring"],
      medications: ["Analgesics", "NSAIDs (avoid early - may delay healing)", "DVT prophylaxis", "Antibiotics for open fractures", "Calcium/Vitamin D", "Tetanus if open fracture"]
    },
    "Osteoarthritis": {
      pathophysiology: "Degenerative joint disease with progressive cartilage deterioration, leading to bone-on-bone friction and joint inflammation.",
      etiology: ["Age", "Obesity", "Joint trauma", "Repetitive stress", "Genetics"],
      clinicalManifestations: ["Joint pain worse with activity", "Morning stiffness <30 minutes", "Crepitus", "Joint enlargement", "Decreased ROM", "Heberden's/Bouchard's nodes"],
      diagnostics: ["X-ray: joint space narrowing, osteophytes", "No specific lab markers", "ESR/CRP normal"],
      nursingInterventions: ["Pain management", "Weight management", "Exercise program", "Joint protection", "Heat/cold application", "Assistive devices"],
      medications: ["Acetaminophen (first-line)", "NSAIDs", "Topical agents", "Intra-articular steroids", "Hyaluronic acid injections"]
    }
  },
  "Hematologic": {
    "Anemia": {
      pathophysiology: "Decreased red blood cells or hemoglobin resulting in reduced oxygen-carrying capacity. Multiple types based on etiology.",
      etiology: ["Iron deficiency", "Chronic disease", "B12/folate deficiency", "Blood loss", "Hemolysis", "Bone marrow failure"],
      clinicalManifestations: ["Fatigue", "Pallor", "Dyspnea on exertion", "Tachycardia", "Dizziness", "Pica (iron deficiency)", "Glossitis"],
      diagnostics: ["CBC: low H&H", "Reticulocyte count", "Iron studies", "B12/folate levels", "Peripheral smear", "Bone marrow biopsy if needed"],
      nursingInterventions: ["Monitor vital signs", "Assess for bleeding", "Fall precautions", "Energy conservation", "Dietary counseling", "Transfusion management"],
      medications: ["Iron supplements (oral or IV)", "B12 injections", "Folic acid", "EPO (for CKD anemia)", "Blood transfusions if severe"]
    }
  },
  "Oncology": {
    "Cancer - General": {
      pathophysiology: "Uncontrolled cell growth with potential for invasion and metastasis. Treatment depends on type, stage, and patient factors.",
      etiology: ["Genetic mutations", "Carcinogens", "Radiation", "Viruses", "Chronic inflammation", "Immune dysfunction"],
      clinicalManifestations: ["Weight loss", "Fatigue", "Pain", "Mass or lesion", "Bleeding", "Paraneoplastic syndromes"],
      diagnostics: ["Biopsy", "Imaging (CT, MRI, PET)", "Tumor markers", "Staging workup", "CBC, CMP"],
      nursingInterventions: ["Pain management", "Nutrition support", "Emotional support", "Treatment education", "Side effect management", "Infection prevention", "Advance directive discussion"],
      medications: ["Chemotherapy agents", "Targeted therapy", "Immunotherapy", "Antiemetics", "Growth factors (Neupogen)", "Pain medications"]
    }
  },
  "Psychiatric": {
    "Altered Mental Status": {
      pathophysiology: "Change in cognitive function that can be acute (delirium) or chronic (dementia). Multiple potential causes requiring thorough evaluation.",
      etiology: ["Infection", "Metabolic disturbance", "Medications", "Substance withdrawal", "Hypoxia", "Dehydration", "Stroke"],
      clinicalManifestations: ["Confusion", "Disorientation", "Agitation", "Lethargy", "Hallucinations", "Fluctuating symptoms"],
      diagnostics: ["CBC, CMP, glucose", "Urinalysis", "TSH", "B12", "Drug screen", "Head CT", "Lumbar puncture if indicated"],
      nursingInterventions: ["Frequent reorientation", "Safety measures", "Calm environment", "Family involvement", "Sleep-wake cycle", "Identify/treat underlying cause"],
      medications: ["Treat underlying cause", "Avoid sedatives if possible", "Low-dose antipsychotics if needed for safety", "Thiamine if alcohol suspected"]
    }
  },
  "Behavioral/Psychiatric": {
    "Major Depressive Disorder": {
      pathophysiology: "Mood disorder characterized by persistent depressed mood, anhedonia, and neurovegetative symptoms. Associated with altered serotonin, norepinephrine, and dopamine neurotransmission.",
      etiology: ["Genetic predisposition", "Neurotransmitter imbalances", "Stressful life events", "Chronic illness", "Substance abuse", "Hormonal changes"],
      clinicalManifestations: ["Depressed mood most of the day", "Anhedonia (loss of interest)", "Sleep disturbances", "Appetite/weight changes", "Fatigue", "Feelings of worthlessness/guilt", "Difficulty concentrating", "Suicidal ideation"],
      diagnostics: ["PHQ-9 screening", "Mental status exam", "Rule out medical causes (TSH, B12, CBC)", "Suicide risk assessment"],
      behaviorAssessment: ["Affect (flat, constricted, tearful)", "Eye contact", "Psychomotor retardation/agitation", "Grooming/hygiene", "Speech pattern (slow, low volume)", "Thought content (hopelessness, worthlessness)"],
      nursingInterventions: ["Suicide precautions if indicated", "Establish therapeutic rapport", "Monitor medication compliance", "Encourage activity/socialization", "Sleep hygiene education", "Safety contract if appropriate"],
      medications: ["SSRIs (Sertraline, Fluoxetine)", "SNRIs (Venlafaxine, Duloxetine)", "Bupropion", "Mirtazapine", "TCAs (rarely first-line)"]
    },
    "Bipolar Disorder - Manic Episode": {
      pathophysiology: "Mood disorder characterized by episodes of mania (elevated mood, increased energy) alternating with depression. Associated with dopamine and norepinephrine dysregulation.",
      etiology: ["Genetic factors (strongest predictor)", "Neurotransmitter imbalances", "Sleep deprivation trigger", "Substance use", "Stressful events"],
      clinicalManifestations: ["Elevated/irritable mood", "Decreased need for sleep", "Pressured speech", "Racing thoughts", "Grandiosity", "Increased goal-directed activity", "Distractibility", "Risky behavior (spending, sexual)"],
      diagnostics: ["Mental status exam", "Mood charting", "Rule out substance-induced mania", "Thyroid function tests", "Drug screen"],
      behaviorAssessment: ["Affect (euphoric, labile, irritable)", "Speech (rapid, loud, difficult to interrupt)", "Motor activity (restless, pacing)", "Thought process (flight of ideas)", "Judgment (impaired)", "Insight (often poor)"],
      nursingInterventions: ["Provide safe, low-stimulation environment", "Set firm, consistent limits", "Redirect grandiose thinking", "Monitor for exhaustion", "Ensure adequate nutrition/hydration", "Medication compliance teaching"],
      medications: ["Lithium (gold standard)", "Valproic acid", "Carbamazepine", "Atypical antipsychotics (Quetiapine, Olanzapine)", "Avoid antidepressants alone"]
    },
    "Schizophrenia": {
      pathophysiology: "Chronic psychotic disorder with positive symptoms (hallucinations, delusions), negative symptoms (flat affect, avolition), and cognitive impairment. Associated with dopamine hyperactivity and glutamate dysfunction.",
      etiology: ["Genetic predisposition", "Dopamine dysregulation", "Prenatal factors", "Neurodevelopmental abnormalities", "Cannabis use in adolescence"],
      clinicalManifestations: ["Auditory hallucinations (most common)", "Paranoid/bizarre delusions", "Disorganized speech", "Flat affect", "Avolition", "Social withdrawal", "Cognitive deficits", "Lack of insight"],
      diagnostics: ["Mental status exam", "Rule out substance use", "Rule out medical causes", "Brain imaging (research)", "Neuropsychological testing"],
      behaviorAssessment: ["Response to internal stimuli", "Eye contact (often poor)", "Affect (flat, inappropriate)", "Thought content (delusions)", "Perceptions (hallucinations)", "Insight/judgment (impaired)"],
      nursingInterventions: ["Build trust without reinforcing delusions", "Do not argue with hallucinations", "Maintain consistent routine", "Monitor for command hallucinations", "Assess suicide risk", "Family education", "Medication compliance support"],
      medications: ["First-generation antipsychotics (Haloperidol)", "Second-generation (Risperidone, Olanzapine, Quetiapine, Clozapine)", "Long-acting injectables for compliance"]
    },
    "Generalized Anxiety Disorder": {
      pathophysiology: "Excessive, persistent worry about multiple life domains causing significant distress. Associated with GABA deficiency and overactive amygdala response.",
      etiology: ["Genetic predisposition", "Neurotransmitter imbalances (GABA, serotonin)", "Chronic stress", "Trauma history", "Personality factors"],
      clinicalManifestations: ["Excessive worry (most days for 6+ months)", "Restlessness", "Fatigue", "Difficulty concentrating", "Muscle tension", "Sleep disturbance", "Irritability"],
      diagnostics: ["GAD-7 screening", "Mental status exam", "Rule out medical causes (thyroid, cardiac)", "Rule out substance-induced anxiety"],
      behaviorAssessment: ["Affect (anxious, worried)", "Motor activity (restless, fidgeting)", "Speech (rapid, worried content)", "Thought content (catastrophizing)", "Physical symptoms (tremor, sweating)", "Avoidance behaviors"],
      nursingInterventions: ["Therapeutic communication", "Teach relaxation techniques", "Cognitive restructuring", "Encourage regular exercise", "Limit caffeine", "Maintain calm environment", "Grounding techniques"],
      medications: ["SSRIs (Sertraline, Escitalopram)", "SNRIs (Venlafaxine, Duloxetine)", "Buspirone", "Benzodiazepines (short-term only)", "Hydroxyzine"]
    },
    "Substance Use Disorder - Alcohol Withdrawal": {
      pathophysiology: "Physiological response to cessation of chronic alcohol use. CNS hyperexcitability due to downregulated GABA and upregulated glutamate receptors. Risk of life-threatening delirium tremens.",
      etiology: ["Chronic heavy alcohol use", "Abrupt cessation", "Previous withdrawal history", "Concurrent medical illness", "Older age"],
      clinicalManifestations: ["Tremor", "Diaphoresis", "Tachycardia", "Hypertension", "Anxiety/agitation", "Nausea/vomiting", "Insomnia", "Hallucinations (visual)", "Seizures (24-48 hrs)", "Delirium tremens (48-72 hrs)"],
      diagnostics: ["CIWA-Ar score monitoring", "Blood alcohol level", "CBC, CMP", "Magnesium level", "Thiamine level", "LFTs"],
      behaviorAssessment: ["Level of consciousness", "Orientation", "Tremor severity", "Diaphoresis", "Agitation level", "Hallucinations (type, response)", "CIWA-Ar scoring Q1-4h"],
      nursingInterventions: ["CIWA-Ar protocol monitoring", "Quiet, well-lit environment", "Frequent reorientation", "Seizure precautions", "Fall precautions", "IV fluid replacement", "Thiamine administration", "Supportive communication"],
      medications: ["Benzodiazepines (Lorazepam, Chlordiazepoxide)", "Thiamine (before glucose)", "Folate", "Multivitamins", "Anticonvulsants if needed"]
    },
    "Suicidal Ideation/Self-Harm": {
      pathophysiology: "Complex interaction of biological, psychological, and social factors leading to thoughts of self-harm or suicide. Associated with serotonin dysfunction, depression, hopelessness.",
      etiology: ["Mental health disorders", "Previous attempts", "Family history of suicide", "Trauma/abuse", "Chronic pain/illness", "Social isolation", "Access to lethal means"],
      clinicalManifestations: ["Expressions of hopelessness", "Giving away possessions", "Social withdrawal", "Mood changes", "Substance use increase", "Sleep disturbances", "Self-harm behaviors", "Suicidal statements"],
      diagnostics: ["Columbia Suicide Severity Rating Scale", "PHQ-9 Item 9", "Mental status exam", "Safety assessment", "Lethal means assessment"],
      behaviorAssessment: ["Current suicidal ideation (frequency, intensity)", "Plan specificity", "Access to means", "Intent", "Protective factors", "Previous attempts", "Hopelessness level", "Social support"],
      nursingInterventions: ["1:1 observation if indicated", "Remove sharps/dangerous items", "Therapeutic presence", "Safety contract (limited evidence)", "Involve family/support system", "Document thoroughly", "Frequent safety checks", "Crisis hotline information"],
      medications: ["Treat underlying condition", "Lithium (reduces suicide risk)", "Clozapine (for schizophrenia)", "Ketamine/Esketamine (acute)", "Avoid prescribing lethal quantities"]
    },
    "Dementia - Behavioral Symptoms": {
      pathophysiology: "Progressive cognitive decline with behavioral and psychological symptoms (BPSD) including agitation, aggression, psychosis. Related to neurodegeneration and neurotransmitter changes.",
      etiology: ["Alzheimer's disease", "Vascular dementia", "Lewy body dementia", "Frontotemporal dementia", "Parkinson's disease dementia"],
      clinicalManifestations: ["Agitation", "Aggression (verbal/physical)", "Wandering", "Sundowning", "Sleep disturbances", "Psychosis (hallucinations, delusions)", "Apathy", "Disinhibition"],
      diagnostics: ["Cognitive assessment (MMSE, MoCA)", "Brain imaging", "Rule out delirium", "Medication review", "Pain assessment"],
      behaviorAssessment: ["Triggers for behaviors", "Time patterns (sundowning)", "Type of agitation", "Response to interventions", "Communication ability", "Baseline behavior vs. change"],
      nursingInterventions: ["Person-centered care", "Identify/address triggers", "Maintain routine", "Calm, unhurried approach", "Redirection", "Validation therapy", "Non-pharmacological interventions first", "Fall precautions"],
      medications: ["Address underlying causes first", "Antipsychotics (low-dose, carefully - black box warning)", "SSRIs for depression/anxiety", "Trazodone for sleep", "Avoid benzodiazepines"]
    },
    "Acute Psychosis": {
      pathophysiology: "Break from reality characterized by hallucinations, delusions, and disorganized thinking. May be due to primary psychiatric disorder, substance use, or medical condition.",
      etiology: ["Schizophrenia", "Bipolar disorder", "Substance-induced", "Medical conditions", "Delirium", "Severe depression with psychosis"],
      clinicalManifestations: ["Hallucinations (auditory most common)", "Delusions (paranoid, grandiose)", "Disorganized speech", "Bizarre behavior", "Impaired reality testing", "Poor insight"],
      diagnostics: ["Mental status exam", "Drug screen", "Medical workup", "Brain imaging if indicated", "Lumbar puncture if infection suspected"],
      behaviorAssessment: ["Type of hallucinations", "Content of delusions", "Level of agitation", "Response to internal stimuli", "Eye contact", "Risk to self/others", "Insight level"],
      nursingInterventions: ["Ensure safety (patient and staff)", "Low-stimulation environment", "Simple, clear communication", "Do not argue with delusions", "Assess for command hallucinations", "PRN medications as ordered", "Document behaviors objectively"],
      medications: ["Antipsychotics (Haloperidol, Olanzapine)", "Benzodiazepines for agitation", "IM formulations if needed"]
    }
  }
};

// Add Psychiatric NANDA Diagnoses
const PSYCH_NANDA_DIAGNOSES = {
  "Risk/Safety": ["Risk for Suicide", "Risk for Self-Harm", "Risk for Other-Directed Violence", "Risk for Self-Directed Violence"],
  "Mood/Affect": ["Hopelessness", "Chronic Low Self-Esteem", "Situational Low Self-Esteem", "Grieving"],
  "Thought/Perception": ["Disturbed Thought Processes", "Disturbed Sensory Perception", "Chronic Confusion", "Acute Confusion"],
  "Coping": ["Ineffective Coping", "Defensive Coping", "Impaired Social Interaction", "Social Isolation"],
  "Self-Care": ["Self-Neglect", "Bathing Self-Care Deficit", "Dressing Self-Care Deficit"],
  "Sleep/Rest": ["Insomnia", "Sleep Deprivation", "Disturbed Sleep Pattern"]
};

// ============================================
// NANDA-I, NOC, NIC LIBRARIES (existing)
// ============================================
const NANDA_DIAGNOSES = {
  "Activity/Rest": ["Activity Intolerance", "Fatigue", "Insomnia", "Impaired Physical Mobility", "Sedentary Lifestyle"],
  "Circulation": ["Decreased Cardiac Output", "Ineffective Peripheral Tissue Perfusion", "Risk for Shock"],
  "Comfort": ["Acute Pain", "Chronic Pain", "Nausea", "Impaired Comfort"],
  "Elimination": ["Constipation", "Diarrhea", "Impaired Urinary Elimination", "Urinary Retention"],
  "Fluid/Electrolyte": ["Deficient Fluid Volume", "Excess Fluid Volume", "Risk for Electrolyte Imbalance"],
  "Nutrition": ["Imbalanced Nutrition: Less Than Body Requirements", "Risk for Unstable Blood Glucose Level", "Risk for Aspiration"],
  "Oxygenation": ["Ineffective Airway Clearance", "Ineffective Breathing Pattern", "Impaired Gas Exchange"],
  "Safety/Protection": ["Risk for Falls", "Risk for Infection", "Impaired Skin Integrity", "Risk for Bleeding"],
  "Cognition/Perception": ["Acute Confusion", "Deficient Knowledge", "Risk for Acute Confusion"],
  "Coping/Stress": ["Anxiety", "Fear", "Ineffective Coping", "Caregiver Role Strain"]
};

const NOC_OUTCOMES = {
  "Physiologic": ["Pain Level", "Respiratory Status", "Cardiac Pump Effectiveness", "Fluid Balance", "Blood Glucose Level", "Tissue Perfusion"],
  "Functional": ["Mobility", "Self-Care Status", "Energy Conservation", "Activity Tolerance"],
  "Safety": ["Fall Prevention Behavior", "Infection Severity", "Risk Control"],
  "Knowledge": ["Knowledge: Disease Process", "Knowledge: Medication", "Knowledge: Treatment Regimen"]
};

const NIC_INTERVENTIONS = {
  "Physiological": ["Pain Management", "Oxygen Therapy", "Fluid Management", "Medication Administration", "Vital Signs Monitoring", "Cardiac Care"],
  "Safety": ["Fall Prevention", "Infection Control", "Surveillance", "Environmental Management"],
  "Behavioral": ["Anxiety Reduction", "Teaching: Disease Process", "Coping Enhancement"],
  "Health System": ["Discharge Planning", "Care Coordination", "Documentation"]
};

// ============================================
// MEDICATIONS DATABASE (5 Rights Practice)
// ============================================
const MEDICATION_DATABASE = {
  "Lisinopril": { class: "ACE Inhibitor", route: "PO", commonDoses: ["2.5mg", "5mg", "10mg", "20mg", "40mg"], frequency: "Daily", considerations: "Monitor BP, K+, renal function. Hold if SBP <100. Dry cough common." },
  "Metoprolol": { class: "Beta-blocker", route: "PO/IV", commonDoses: ["25mg", "50mg", "100mg"], frequency: "BID", considerations: "Monitor HR, BP. Hold if HR <60. Do not stop abruptly." },
  "Furosemide": { class: "Loop Diuretic", route: "PO/IV", commonDoses: ["20mg", "40mg", "80mg"], frequency: "Daily-BID", considerations: "Monitor I&O, K+, daily weight. Give in AM to avoid nocturia." },
  "Morphine": { class: "Opioid Analgesic", route: "PO/IV/IM", commonDoses: ["2mg", "4mg", "10mg", "15mg"], frequency: "Q4H PRN", considerations: "Assess pain before/after. Monitor RR, sedation. Naloxone at bedside." },
  "Heparin": { class: "Anticoagulant", route: "IV/SubQ", commonDoses: ["5000 units", "7500 units", "varies by protocol"], frequency: "Q8-12H or continuous", considerations: "Monitor PTT, signs of bleeding. Protamine is antidote." },
  "Metformin": { class: "Biguanide", route: "PO", commonDoses: ["500mg", "850mg", "1000mg"], frequency: "BID with meals", considerations: "Hold before contrast. Monitor renal function. GI upset common." },
  "Insulin Regular": { class: "Short-acting Insulin", route: "IV/SubQ", commonDoses: ["varies by sliding scale"], frequency: "AC and HS or continuous", considerations: "Check glucose before. Verify dose with another nurse. Have D50 available." },
  "Albuterol": { class: "Short-acting Beta-2 Agonist", route: "Inhaled", commonDoses: ["2.5mg nebulizer", "2 puffs MDI"], frequency: "Q4-6H or PRN", considerations: "May cause tachycardia, tremors. Rinse mouth after." },
  "Pantoprazole": { class: "Proton Pump Inhibitor", route: "PO/IV", commonDoses: ["40mg"], frequency: "Daily-BID", considerations: "Give 30-60 min before meals. Long-term: risk of C. diff, fractures." },
  "Levothyroxine": { class: "Thyroid Hormone", route: "PO", commonDoses: ["25mcg", "50mcg", "75mcg", "100mcg", "125mcg"], frequency: "Daily", considerations: "Take on empty stomach. Monitor TSH. Many drug interactions." },
  "Warfarin": { class: "Anticoagulant", route: "PO", commonDoses: ["2mg", "5mg", "7.5mg", "10mg"], frequency: "Daily", considerations: "Monitor INR (goal 2-3). Vitamin K is antidote. Many food/drug interactions." },
  "Digoxin": { class: "Cardiac Glycoside", route: "PO/IV", commonDoses: ["0.125mg", "0.25mg"], frequency: "Daily", considerations: "Check apical pulse x1 min. Hold if HR <60. Narrow therapeutic window." }
};

// ============================================
// LAB VALUES REFERENCE
// ============================================
const LAB_REFERENCE = {
  "CBC": {
    "WBC": { normal: "4,500-11,000/μL", normalMin: 4.5, normalMax: 11.0, unit: "x10³/μL", high: "Infection, leukemia, stress", low: "Bone marrow suppression, immunodeficiency" },
    "RBC": { normal: "M: 4.7-6.1, F: 4.2-5.4 million/μL", normalMin: 4.2, normalMax: 5.4, unit: "million/μL", high: "Polycythemia, dehydration", low: "Anemia, blood loss" },
    "Hemoglobin": { normal: "M: 14-18, F: 12-16 g/dL", normalMin: 12, normalMax: 16, unit: "g/dL", high: "Polycythemia, COPD", low: "Anemia, hemorrhage" },
    "Hematocrit": { normal: "M: 42-52%, F: 37-47%", normalMin: 37, normalMax: 47, unit: "%", high: "Dehydration, polycythemia", low: "Anemia, overhydration" },
    "Platelets": { normal: "150,000-400,000/μL", normalMin: 150, normalMax: 400, unit: "x10³/μL", high: "Infection, malignancy", low: "Thrombocytopenia, bleeding risk" }
  },
  "BMP": {
    "Sodium": { normal: "136-145 mEq/L", normalMin: 136, normalMax: 145, unit: "mEq/L", high: "Dehydration, diabetes insipidus", low: "SIADH, heart failure, diuretics" },
    "Potassium": { normal: "3.5-5.0 mEq/L", normalMin: 3.5, normalMax: 5.0, unit: "mEq/L", high: "Renal failure, ACE inhibitors", low: "Diuretics, vomiting, diarrhea" },
    "Chloride": { normal: "98-106 mEq/L", normalMin: 98, normalMax: 106, unit: "mEq/L", high: "Dehydration, metabolic acidosis", low: "Vomiting, metabolic alkalosis" },
    "CO2": { normal: "23-29 mEq/L", normalMin: 23, normalMax: 29, unit: "mEq/L", high: "Metabolic alkalosis, COPD", low: "Metabolic acidosis, DKA" },
    "BUN": { normal: "10-20 mg/dL", normalMin: 10, normalMax: 20, unit: "mg/dL", high: "Renal dysfunction, dehydration, GI bleed", low: "Malnutrition, liver disease" },
    "Creatinine": { normal: "0.7-1.3 mg/dL", normalMin: 0.7, normalMax: 1.3, unit: "mg/dL", high: "Renal dysfunction", low: "Decreased muscle mass" },
    "Glucose": { normal: "70-100 mg/dL (fasting)", normalMin: 70, normalMax: 100, unit: "mg/dL", high: "Diabetes, stress, steroids", low: "Insulin excess, sepsis" }
  },
  "Cardiac": {
    "Troponin I": { normal: "<0.04 ng/mL", normalMin: 0, normalMax: 0.04, unit: "ng/mL", high: "MI, myocardial injury", low: "N/A" },
    "BNP": { normal: "<100 pg/mL", normalMin: 0, normalMax: 100, unit: "pg/mL", high: "Heart failure", low: "N/A" },
    "CK-MB": { normal: "0-3% of total CK", normalMin: 0, normalMax: 3, unit: "%", high: "MI (peaks 12-24hr)", low: "N/A" }
  },
  "Coagulation": {
    "PT": { normal: "11-13 seconds", normalMin: 11, normalMax: 13, unit: "sec", high: "Warfarin, liver disease, Vit K deficiency", low: "N/A" },
    "INR": { normal: "0.8-1.1 (2-3 on warfarin)", normalMin: 0.8, normalMax: 1.1, unit: "", high: "Over-anticoagulation", low: "Under-anticoagulation" },
    "PTT": { normal: "25-35 seconds", normalMin: 25, normalMax: 35, unit: "sec", high: "Heparin therapy, hemophilia", low: "N/A" }
  },
  "ABG": {
    "pH": { normal: "7.35-7.45", normalMin: 7.35, normalMax: 7.45, unit: "", high: "Alkalosis", low: "Acidosis" },
    "PaCO2": { normal: "35-45 mmHg", normalMin: 35, normalMax: 45, unit: "mmHg", high: "Respiratory acidosis", low: "Respiratory alkalosis" },
    "HCO3": { normal: "22-26 mEq/L", normalMin: 22, normalMax: 26, unit: "mEq/L", high: "Metabolic alkalosis", low: "Metabolic acidosis" },
    "PaO2": { normal: "80-100 mmHg", normalMin: 80, normalMax: 100, unit: "mmHg", high: "O2 therapy", low: "Hypoxemia" }
  }
};

// Condition-specific abnormal lab values
const CONDITION_LAB_VALUES = {
  "Congestive Heart Failure (CHF)": {
    "BNP": { value: 850, status: "critical-high" },
    "Sodium": { value: 132, status: "low" },
    "Potassium": { value: 3.2, status: "low" },
    "Creatinine": { value: 1.6, status: "high" },
    "BUN": { value: 28, status: "high" },
    "Troponin I": { value: 0.02, status: "normal" }
  },
  "Acute Myocardial Infarction (MI)": {
    "Troponin I": { value: 2.8, status: "critical-high" },
    "CK-MB": { value: 12, status: "critical-high" },
    "BNP": { value: 450, status: "high" },
    "Potassium": { value: 4.2, status: "normal" },
    "Glucose": { value: 165, status: "high" }
  },
  "Acute Kidney Injury (AKI)": {
    "Creatinine": { value: 3.8, status: "critical-high" },
    "BUN": { value: 58, status: "critical-high" },
    "Potassium": { value: 5.8, status: "critical-high" },
    "Sodium": { value: 134, status: "low" },
    "CO2": { value: 18, status: "low" },
    "pH": { value: 7.28, status: "low" }
  },
  "Chronic Kidney Disease (CKD)": {
    "Creatinine": { value: 4.2, status: "critical-high" },
    "BUN": { value: 65, status: "critical-high" },
    "Potassium": { value: 5.5, status: "high" },
    "Hemoglobin": { value: 9.2, status: "low" },
    "Hematocrit": { value: 28, status: "low" },
    "CO2": { value: 19, status: "low" }
  },
  "Diabetes Mellitus Type 2": {
    "Glucose": { value: 245, status: "critical-high" },
    "Potassium": { value: 4.8, status: "normal" },
    "Creatinine": { value: 1.4, status: "high" },
    "BUN": { value: 22, status: "high" }
  },
  "Diabetic Ketoacidosis (DKA)": {
    "Glucose": { value: 485, status: "critical-high" },
    "pH": { value: 7.18, status: "critical-low" },
    "CO2": { value: 12, status: "critical-low" },
    "HCO3": { value: 10, status: "critical-low" },
    "Potassium": { value: 5.9, status: "high" },
    "Sodium": { value: 128, status: "low" },
    "BUN": { value: 32, status: "high" }
  },
  "Sepsis/Septic Shock": {
    "WBC": { value: 18.5, status: "critical-high" },
    "Glucose": { value: 168, status: "high" },
    "Creatinine": { value: 2.1, status: "high" },
    "pH": { value: 7.28, status: "low" },
    "PaCO2": { value: 28, status: "low" },
    "Platelets": { value: 95, status: "low" }
  },
  "Chronic Obstructive Pulmonary Disease (COPD)": {
    "pH": { value: 7.32, status: "low" },
    "PaCO2": { value: 58, status: "high" },
    "PaO2": { value: 62, status: "low" },
    "HCO3": { value: 32, status: "high" },
    "Hemoglobin": { value: 17.5, status: "high" },
    "Hematocrit": { value: 52, status: "high" }
  },
  "Pneumonia": {
    "WBC": { value: 15.8, status: "high" },
    "PaO2": { value: 68, status: "low" },
    "pH": { value: 7.48, status: "high" },
    "PaCO2": { value: 30, status: "low" }
  },
  "Pulmonary Embolism (PE)": {
    "PaO2": { value: 58, status: "critical-low" },
    "PaCO2": { value: 28, status: "low" },
    "pH": { value: 7.48, status: "high" },
    "Troponin I": { value: 0.15, status: "high" },
    "BNP": { value: 280, status: "high" }
  },
  "GI Bleeding": {
    "Hemoglobin": { value: 7.8, status: "critical-low" },
    "Hematocrit": { value: 24, status: "critical-low" },
    "BUN": { value: 45, status: "high" },
    "Creatinine": { value: 1.1, status: "normal" },
    "Platelets": { value: 185, status: "normal" }
  },
  "Liver Cirrhosis": {
    "Platelets": { value: 85, status: "low" },
    "PT": { value: 18, status: "high" },
    "INR": { value: 1.8, status: "high" },
    "Sodium": { value: 128, status: "low" },
    "Hemoglobin": { value: 10.2, status: "low" },
    "BUN": { value: 8, status: "low" }
  },
  "Stroke (CVA)": {
    "Glucose": { value: 142, status: "high" },
    "Sodium": { value: 138, status: "normal" },
    "Potassium": { value: 4.0, status: "normal" },
    "PT": { value: 12, status: "normal" },
    "INR": { value: 1.0, status: "normal" }
  },
  "Deep Vein Thrombosis (DVT)": {
    "PT": { value: 12, status: "normal" },
    "PTT": { value: 42, status: "high" },
    "INR": { value: 1.0, status: "normal" },
    "Platelets": { value: 220, status: "normal" }
  },
  "Hypertension": {
    "Creatinine": { value: 1.4, status: "high" },
    "BUN": { value: 24, status: "high" },
    "Potassium": { value: 4.5, status: "normal" },
    "Glucose": { value: 108, status: "high" }
  },
  "Hypothyroidism": {
    "Sodium": { value: 132, status: "low" },
    "Cholesterol": { value: 245, status: "high" },
    "Glucose": { value: 78, status: "normal" },
    "Hemoglobin": { value: 11.5, status: "low" }
  },
  "Hip Fracture": {
    "Hemoglobin": { value: 10.2, status: "low" },
    "Hematocrit": { value: 32, status: "low" },
    "WBC": { value: 11.5, status: "high" },
    "Platelets": { value: 195, status: "normal" },
    "Creatinine": { value: 0.9, status: "normal" }
  },
  "Anemia": {
    "Hemoglobin": { value: 8.5, status: "critical-low" },
    "Hematocrit": { value: 26, status: "critical-low" },
    "RBC": { value: 3.2, status: "low" },
    "WBC": { value: 6.5, status: "normal" },
    "Platelets": { value: 210, status: "normal" }
  },
  "Post-Operative Care": {
    "WBC": { value: 12.5, status: "high" },
    "Hemoglobin": { value: 11.0, status: "low" },
    "Hematocrit": { value: 34, status: "low" },
    "Glucose": { value: 145, status: "high" },
    "Potassium": { value: 3.8, status: "normal" }
  },
  "Atrial Fibrillation": {
    "Potassium": { value: 3.2, status: "low" },
    "Magnesium": { value: 1.4, status: "low" },
    "TSH": { value: 0.3, status: "low" },
    "BNP": { value: 180, status: "high" }
  },
  "Acute Myocardial Infarction (MI)": {
    "Troponin I": { value: 2.5, status: "critical-high" },
    "CK-MB": { value: 8, status: "high" },
    "BNP": { value: 320, status: "high" },
    "Glucose": { value: 165, status: "high" },
    "WBC": { value: 12.0, status: "high" }
  },
  // Psychiatric Labs (mostly normal, rule out medical causes)
  "Major Depressive Disorder": {
    "TSH": { value: 2.1, status: "normal" },
    "Glucose": { value: 95, status: "normal" },
    "Sodium": { value: 139, status: "normal" },
    "B12": { value: 450, status: "normal" }
  },
  "Substance Use Disorder - Alcohol Withdrawal": {
    "Magnesium": { value: 1.2, status: "low" },
    "Potassium": { value: 3.0, status: "low" },
    "Glucose": { value: 72, status: "low" },
    "GGT": { value: 185, status: "high" },
    "AST": { value: 95, status: "high" },
    "ALT": { value: 68, status: "high" },
    "MCV": { value: 102, status: "high" }
  },
  "Schizophrenia": {
    "Glucose": { value: 105, status: "normal" },
    "Prolactin": { value: 45, status: "high" },
    "WBC": { value: 7.5, status: "normal" }
  }
};

// ============================================
// GRADING CRITERIA
// ============================================
const GRADING_CRITERIA = {
  assessment: {
    name: "Assessment Accuracy",
    icon: ClipboardCheck,
    maxPoints: 100,
    items: [
      { action: "complete_vital_signs", points: 15, description: "Reviewed all vital signs" },
      { action: "assess_pain", points: 15, description: "Assessed pain level (0-10)" },
      { action: "physical_assessment", points: 20, description: "Completed head-to-toe assessment" },
      { action: "review_history", points: 15, description: "Reviewed medical history" },
      { action: "identify_abnormals", points: 20, description: "Identified abnormal findings" },
      { action: "prioritize_concerns", points: 15, description: "Prioritized patient concerns" }
    ]
  },
  medication: {
    name: "Medication Safety (5 Rights)",
    icon: Pill,
    maxPoints: 100,
    items: [
      { action: "verify_patient", points: 20, description: "Verified right patient (2 identifiers)" },
      { action: "verify_drug", points: 20, description: "Verified right drug" },
      { action: "verify_dose", points: 20, description: "Verified right dose" },
      { action: "verify_route", points: 20, description: "Verified right route" },
      { action: "verify_time", points: 20, description: "Verified right time" }
    ]
  },
  infection: {
    name: "Infection Control",
    icon: Shield,
    maxPoints: 100,
    items: [
      { action: "hand_hygiene", points: 30, description: "Performed hand hygiene" },
      { action: "ppe_donning", points: 20, description: "Donned appropriate PPE" },
      { action: "sterile_technique", points: 25, description: "Maintained sterile technique" },
      { action: "isolation_precautions", points: 25, description: "Followed isolation precautions" }
    ]
  },
  communication: {
    name: "Patient Communication",
    icon: MessageSquare,
    maxPoints: 100,
    items: [
      { action: "introduce_self", points: 15, description: "Introduced self and role" },
      { action: "explain_procedures", points: 20, description: "Explained procedures/treatments" },
      { action: "active_listening", points: 20, description: "Demonstrated active listening" },
      { action: "education_provided", points: 25, description: "Provided patient education" },
      { action: "addressed_concerns", points: 20, description: "Addressed patient concerns" }
    ]
  },
  documentation: {
    name: "Documentation Quality",
    icon: FileText,
    maxPoints: 100,
    items: [
      { action: "timely_documentation", points: 25, description: "Documented in timely manner" },
      { action: "accurate_charting", points: 25, description: "Accurate and complete charting" },
      { action: "nursing_notes", points: 25, description: "Written nursing notes" },
      { action: "care_plan_updated", points: 25, description: "Updated care plan" }
    ]
  }
};

// Helper function to determine auscultation sounds based on patient condition
const getPatientAuscultationSounds = (patient, matchedCondition) => {
  if (!patient) return { lung: null, heart: null, bowel: null };
  
  const diagnosis = patient.diagnosis?.toLowerCase() || '';
  const category = patient.category?.toLowerCase() || '';
  
  let lungSound = 'normal';
  let heartSound = 'normal_s1s2';
  let bowelSound = 'normal';
  
  // Cardiovascular conditions
  if (diagnosis.includes('chf') || diagnosis.includes('heart failure') || diagnosis.includes('pulmonary edema')) {
    lungSound = 'crackles_fine';
    heartSound = 's3_gallop';
  } else if (diagnosis.includes('mi') || diagnosis.includes('acs') || diagnosis.includes('coronary')) {
    lungSound = 'crackles_fine';
    heartSound = 'murmur_systolic';
  } else if (diagnosis.includes('afib') || diagnosis.includes('arrhythmia')) {
    heartSound = 'murmur_systolic';
  }
  
  // Respiratory conditions
  if (diagnosis.includes('pneumonia')) {
    lungSound = 'crackles_coarse';
  } else if (diagnosis.includes('copd') || diagnosis.includes('emphysema')) {
    lungSound = 'wheezes';
  } else if (diagnosis.includes('asthma')) {
    lungSound = 'wheezes';
  } else if (diagnosis.includes('pneumothorax')) {
    lungSound = 'diminished';
  } else if (diagnosis.includes('pleural effusion') || diagnosis.includes('pleurisy')) {
    lungSound = 'pleural_friction_rub';
  } else if (diagnosis.includes('bronchitis')) {
    lungSound = 'rhonchi';
  }
  
  // GI conditions
  if (diagnosis.includes('obstruction') || diagnosis.includes('ileus')) {
    bowelSound = 'hypoactive';
  } else if (diagnosis.includes('diarrhea') || diagnosis.includes('gastroenteritis')) {
    bowelSound = 'hyperactive';
  } else if (diagnosis.includes('peritonitis') || diagnosis.includes('post-op')) {
    bowelSound = 'hypoactive';
  }
  
  // Post-surgical
  if (diagnosis.includes('surgery') || diagnosis.includes('post-op') || diagnosis.includes('hip') || diagnosis.includes('cabg')) {
    bowelSound = 'hypoactive';
  }
  
  return { lung: lungSound, heart: heartSound, bowel: bowelSound };
};

// ============================================
// MAIN SIMULATION ROOM COMPONENT
// ============================================
const SimulationRoom = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionInput, setActionInput] = useState('');
  const [actionType, setActionType] = useState('Assessment');
  const [submitting, setSubmitting] = useState(false);
  const [responses, setResponses] = useState([]);
  const [activeMainTab, setActiveMainTab] = useState('overview');
  
  // Scoring State
  const [scores, setScores] = useState({
    assessment: { earned: 0, actions: [] },
    medication: { earned: 0, actions: [] },
    infection: { earned: 0, actions: [] },
    communication: { earned: 0, actions: [] },
    documentation: { earned: 0, actions: [] }
  });
  
  // Care Plan State
  const [carePlan, setCarePlan] = useState({
    nursing_diagnoses: [{ diagnosis: '', related_to: '', evidence: '' }],
    goals: [{ outcome: '', timeframe: '', indicators: '' }],
    interventions: [{ intervention: '', rationale: '', frequency: '' }],
    evaluations: [{ criteria: '', met: '', notes: '' }]
  });
  const [savingCarePlan, setSavingCarePlan] = useState(false);
  
  // Medication Administration State
  const [medAdmin, setMedAdmin] = useState({
    selectedMed: '',
    patientVerified: false,
    drugVerified: false,
    doseVerified: false,
    routeVerified: false,
    timeVerified: false
  });
  
  // Get matched condition for patient
  const [matchedCondition, setMatchedCondition] = useState(null);
  
  // Patient-specific lab values state
  const [patientLabValues, setPatientLabValues] = useState({});
  
  // SBAR and MD Orders State
  const [sbarForm, setSbarForm] = useState({
    situation: '',
    background: '',
    assessment: '',
    recommendation: ''
  });
  const [submittingSbar, setSubmittingSbar] = useState(false);
  const [mdOrders, setMdOrders] = useState([]);
  const [mdResponses, setMdResponses] = useState([]);
  
  // Order Execution State
  const [executingOrderId, setExecutingOrderId] = useState(null);
  const [executedOrders, setExecutedOrders] = useState([]);
  const [patientUpdates, setPatientUpdates] = useState([]);
  const [currentVitals, setCurrentVitals] = useState(null);
  
  // Time Progression State
  const [simulationTime, setSimulationTime] = useState(0); // minutes elapsed
  const [advancingTime, setAdvancingTime] = useState(false);
  const [timeEvents, setTimeEvents] = useState([]);
  
  // Behavior Notes State (for Psychiatric Patients)
  const [behaviorNotes, setBehaviorNotes] = useState([]);
  const [currentBehaviorNote, setCurrentBehaviorNote] = useState({
    timestamp: '',
    behavior_observed: '',
    patient_response: '',
    nurse_intervention: '',
    outcome: '',
    mood_affect: '',
    safety_concerns: ''
  });
  const [isPsychPatient, setIsPsychPatient] = useState(false);
  const [scoringNote, setScoringNote] = useState(false);
  const [lastNoteScore, setLastNoteScore] = useState(null);
  
  // Comprehensive Nursing Note State
  const [nursingNote, setNursingNote] = useState({
    assessment_findings: '',
    vital_signs_interpretation: '',
    nursing_diagnoses: '',
    interventions_performed: '',
    patient_response: '',
    clinical_reasoning: '',
    plan_of_care: '',
    patient_education: ''
  });
  const [scoringNursingNote, setScoringNursingNote] = useState(false);
  const [nursingNoteScore, setNursingNoteScore] = useState(null);
  const [nursingNoteHistory, setNursingNoteHistory] = useState([]);

  // Calculate overall score
  const calculateOverallScore = useCallback(() => {
    const categories = Object.values(scores);
    const totalEarned = categories.reduce((sum, cat) => sum + cat.earned, 0);
    const totalPossible = Object.values(GRADING_CRITERIA).reduce((sum, cat) => sum + cat.maxPoints, 0);
    return Math.round((totalEarned / totalPossible) * 100);
  }, [scores]);

  // Calculate category score percentage
  const getCategoryScore = (category) => {
    const criteria = GRADING_CRITERIA[category];
    if (!criteria) return 0;
    return Math.round((scores[category].earned / criteria.maxPoints) * 100);
  };

  // Add points to a category
  const addScore = (category, action, points, description) => {
    setScores(prev => {
      // Check if action already recorded
      if (prev[category].actions.includes(action)) {
        return prev;
      }
      return {
        ...prev,
        [category]: {
          earned: Math.min(prev[category].earned + points, GRADING_CRITERIA[category].maxPoints),
          actions: [...prev[category].actions, action]
        }
      };
    });
    toast.success(`+${points} points: ${description}`, { duration: 2000 });
  };

  // Load session data
  useEffect(() => {
    const loadSession = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/sessions/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSession(response.data);
        // Map patient_name to name for compatibility
        const patientData = response.data.patient;
        if (patientData && patientData.patient_name) {
          patientData.name = patientData.patient_name;
        }
        setPatient(patientData);
        
        // Match patient diagnosis to condition database using comprehensive NCLEX-aligned matching
        const diagnosis = (response.data.patient?.diagnosis || '').toLowerCase();
        let bestMatch = null;
        let bestScore = 0;
        
        // Comprehensive keyword map with NCLEX-standard terminology and common variations
        const keywordMap = {
          // Cardiovascular
          "Congestive Heart Failure (CHF)": [
            "chf", "heart failure", "congestive heart", "cardiac failure", "left ventricular failure", 
            "right heart failure", "hf ", "hfref", "hfpef", "decompensated heart", "acute heart failure",
            "chronic heart failure", "cardiomyopathy", "systolic dysfunction", "diastolic dysfunction",
            "fluid overload", "pulmonary edema", "cardiac decompensation", "exacerbation of chf",
            "acute exacerbation", "nyha class", "ejection fraction", "volume overload"
          ],
          "Acute Myocardial Infarction (MI)": [
            "myocardial infarction", "heart attack", "stemi", "nstemi", " mi ", "coronary occlusion",
            "acute coronary syndrome", "acs", "chest pain cardiac", "troponin elevation", "ami",
            "st elevation", "non-st elevation", "coronary artery disease", "angina unstable"
          ],
          "Hypertension": [
            "hypertension", "high blood pressure", "htn", "elevated bp", "hypertensive", 
            "hypertensive crisis", "hypertensive urgency", "hypertensive emergency", "malignant hypertension",
            "uncontrolled blood pressure", "resistant hypertension"
          ],
          "Deep Vein Thrombosis (DVT)": [
            "dvt", "deep vein thrombosis", "venous thrombosis", "leg clot", "lower extremity clot",
            "venous thromboembolism", "vte", "iliofemoral thrombosis", "calf vein thrombosis",
            "proximal dvt", "distal dvt"
          ],
          // Respiratory
          "Chronic Obstructive Pulmonary Disease (COPD)": [
            "copd", "chronic obstructive", "emphysema", "chronic bronchitis", "copd exacerbation",
            "acute exacerbation of copd", "aecopd", "obstructive lung disease", "chronic airway",
            "gold stage", "fev1", "barrel chest", "chronic respiratory", "copd flare"
          ],
          "Pneumonia": [
            "pneumonia", "lung infection", "community acquired pneumonia", "hospital acquired pneumonia",
            "cap", "hap", "vap", "ventilator associated", "aspiration pneumonia", "lobar pneumonia",
            "bronchopneumonia", "respiratory infection", "lung consolidation", "bacterial pneumonia",
            "viral pneumonia", "pneumonitis"
          ],
          "Asthma": [
            "asthma", "bronchospasm", "reactive airway", "asthmatic", "asthma exacerbation",
            "acute asthma", "status asthmaticus", "wheezing", "bronchial asthma", "allergic asthma",
            "exercise induced asthma"
          ],
          "Pulmonary Embolism (PE)": [
            "pulmonary embolism", " pe ", "lung clot", "pulmonary thromboembolism", "saddle embolus",
            "submassive pe", "massive pe", "acute pe", "pulmonary embolus", "v/q mismatch",
            "pulmonary infarction"
          ],
          // Endocrine
          "Diabetes Mellitus Type 2": [
            "diabetes mellitus", "type 2 diabetes", "t2dm", "dm2", "diabetic", "hyperglycemia",
            "type ii diabetes", "niddm", "insulin resistance", "uncontrolled diabetes", "diabetes type 2",
            "glucose intolerance", "high blood sugar", "a1c elevated", "poorly controlled diabetes"
          ],
          "Diabetic Ketoacidosis (DKA)": [
            "dka", "ketoacidosis", "diabetic ketoacidosis", "diabetic acidosis", "ketotic",
            "metabolic acidosis diabetic", "anion gap acidosis"
          ],
          "Hypothyroidism": [
            "hypothyroid", "low thyroid", "hashimoto", "myxedema", "thyroid deficiency",
            "underactive thyroid", "elevated tsh", "low t4", "thyroid insufficiency"
          ],
          "Hyperthyroidism": [
            "hyperthyroid", "graves", "thyroid storm", "thyrotoxicosis", "overactive thyroid",
            "suppressed tsh", "elevated t4", "toxic goiter"
          ],
          // Renal
          "Acute Kidney Injury (AKI)": [
            "aki", "acute kidney injury", "acute renal failure", "acute renal injury", "arf",
            "elevated creatinine", "rising creatinine", "oliguria", "acute tubular necrosis",
            "atn", "prerenal azotemia", "intrinsic renal", "postrenal"
          ],
          "Chronic Kidney Disease (CKD)": [
            "ckd", "chronic kidney", "chronic renal", "esrd", "end stage renal", "kidney disease",
            "renal insufficiency", "stage 3 ckd", "stage 4 ckd", "stage 5 ckd", "dialysis dependent",
            "uremia", "decreased gfr", "renal failure chronic"
          ],
          // Gastrointestinal
          "GI Bleeding": [
            "gi bleed", "gastrointestinal bleed", "melena", "hematemesis", "hematochezia", 
            "upper gi bleed", "lower gi bleed", "ugib", "lgib", "rectal bleeding", "bloody stool",
            "coffee ground emesis", "black tarry stool", "variceal bleeding", "peptic ulcer bleed"
          ],
          "Liver Cirrhosis": [
            "cirrhosis", "liver failure", "hepatic failure", "hepatic encephalopathy", "ascites",
            "liver disease", "portal hypertension", "esophageal varices", "hepatorenal",
            "decompensated cirrhosis", "alcoholic liver", "nash cirrhosis", "jaundice hepatic",
            "coagulopathy liver"
          ],
          "Pancreatitis": [
            "pancreatitis", "acute pancreatitis", "chronic pancreatitis", "pancreatic inflammation",
            "elevated lipase", "elevated amylase", "necrotizing pancreatitis", "gallstone pancreatitis"
          ],
          // Neurological
          "Stroke (CVA)": [
            "stroke", "cva", "cerebrovascular accident", "hemiplegia", "hemiparesis", "ischemic stroke",
            "hemorrhagic stroke", "brain attack", "tia", "transient ischemic", "embolic stroke",
            "thrombotic stroke", "lacunar infarct", "intracerebral hemorrhage", "ich", "sah",
            "subarachnoid hemorrhage", "aphasia", "facial droop", "arm weakness"
          ],
          "Seizures": [
            "seizure", "epilepsy", "convulsion", "status epilepticus", "tonic clonic", "grand mal",
            "petit mal", "absence seizure", "focal seizure", "partial seizure", "breakthrough seizure",
            "new onset seizure"
          ],
          // Infection
          "Sepsis/Septic Shock": [
            "sepsis", "septic shock", "severe sepsis", "bacteremia", "sirs", "systemic inflammatory",
            "urosepsis", "septicemia", "blood stream infection", "sepsis protocol", "qsofa",
            "lactate elevated", "sepsis source"
          ],
          // Post-Operative
          "Post-Operative Care": [
            "post-op", "postoperative", "post operative", "surgical recovery", "after surgery",
            "postprocedure", "s/p surgery", "status post", "day 1 post", "pod #", "surgical site",
            "pod 1", "cabg", "laparoscopic", "appendectomy", "total hip replacement", "hip replacement"
          ],
          // Musculoskeletal
          "Hip Fracture": [
            "hip fracture", "fractured hip", "femoral neck fracture", "intertrochanteric fracture",
            "broken hip", "hip fx", "nof fracture", "subcapital fracture", "right hip fracture",
            "left hip fracture", "orif hip", "hip surgery", "hip replacement"
          ],
          "Lumbar Radiculopathy": [
            "radiculopathy", "lumbar radiculopathy", "sciatica", "herniated disc", "herniated disk",
            "disc herniation", "disk herniation", "back pain", "lower back pain", "lumbago",
            "nerve root compression", "spinal stenosis", "degenerative disc", "pinched nerve",
            "lumbar stenosis", "cauda equina", "leg pain radiating", "numbness leg"
          ],
          "Spinal Cord Injury": [
            "spinal cord injury", "sci", "paraplegia", "quadriplegia", "tetraplegia",
            "spinal fracture", "vertebral fracture", "cord compression", "spinal trauma",
            "cervical injury", "thoracic injury", "neurogenic shock", "spinal shock"
          ],
          "Osteoarthritis": [
            "osteoarthritis", "oa", "degenerative joint", "djd", "arthritis", "joint pain",
            "knee replacement", "total knee", "tka", "tkr", "joint replacement"
          ],
          "Fractures (General)": [
            "fracture", "broken bone", "fx", "orif", "closed reduction", "cast",
            "tibial fracture", "femur fracture", "ankle fracture", "wrist fracture",
            "colles fracture", "compression fracture", "pathological fracture"
          ],
          // Gastrointestinal - Additional
          "Cholecystitis": [
            "cholecystitis", "gallbladder", "cholelithiasis", "gallstones", "biliary colic",
            "acute cholecystitis", "chronic cholecystitis", "cholangitis", "biliary"
          ],
          // Neurological - Additional
          "Traumatic Brain Injury": [
            "tbi", "traumatic brain injury", "head injury", "brain injury", "concussion",
            "intracranial hemorrhage", "subdural hematoma", "epidural hematoma", "evd",
            "icp monitoring", "brain trauma", "head trauma", "closed head injury"
          ],
          // Pediatric
          "Bronchiolitis": [
            "bronchiolitis", "rsv", "respiratory syncytial virus", "viral bronchiolitis",
            "infant respiratory", "pediatric respiratory"
          ],
          "Febrile Seizure": [
            "febrile seizure", "fever seizure", "pediatric seizure", "fever with seizure"
          ],
          // Additional common NCLEX conditions
          "Atrial Fibrillation": [
            "atrial fibrillation", "afib", "a fib", "a-fib", "rapid afib", "rvr", "irregular rhythm"
          ],
          "Cellulitis": [
            "cellulitis", "skin infection", "soft tissue infection", "erythema spreading",
            "infected wound"
          ],
          "Urinary Tract Infection": [
            "uti", "urinary tract infection", "pyelonephritis", "cystitis", "bladder infection",
            "kidney infection", "uti source"
          ],
          "Falls": [
            "fall", "mechanical fall", "syncope", "near syncope", "found down", "ground level fall"
          ],
          // Psychiatric/Behavioral
          "Major Depressive Disorder": [
            "depression", "major depressive", "mdd", "depressed", "depressive disorder", "suicidal ideation",
            "depressive episode", "major depression", "clinical depression", "unipolar depression"
          ],
          "Bipolar Disorder - Manic Episode": [
            "bipolar", "manic", "mania", "bipolar disorder", "manic episode", "bipolar i", "bipolar ii",
            "mood disorder", "cyclothymia", "hypomania"
          ],
          "Schizophrenia": [
            "schizophrenia", "psychosis", "psychotic", "schizophrenic", "schizoaffective",
            "hallucinations", "delusions", "paranoid schizophrenia", "thought disorder"
          ],
          "Generalized Anxiety Disorder": [
            "anxiety", "gad", "generalized anxiety", "panic", "panic attack", "anxious",
            "anxiety disorder", "panic disorder", "phobia"
          ],
          "Substance Use Disorder - Alcohol Withdrawal": [
            "alcohol withdrawal", "etoh withdrawal", "detox", "withdrawal", "dts", "delirium tremens",
            "ciwa", "alcohol use disorder", "alcoholism", "substance abuse", "substance use"
          ],
          "Suicidal Ideation/Self-Harm": [
            "suicidal", "suicide", "self-harm", "self harm", "si", "cutting", "overdose attempt",
            "suicide attempt", "psychiatric emergency"
          ],
          "Dementia - Behavioral Symptoms": [
            "dementia", "alzheimer", "cognitive decline", "memory loss", "behavioral symptoms",
            "agitation", "sundowning", "wandering", "bpsd"
          ],
          "Acute Psychosis": [
            "acute psychosis", "psychotic break", "first episode psychosis", "brief psychotic",
            "psychotic episode"
          ]
        };
        
        // Process diagnosis to clean it for matching
        const cleanDiagnosis = diagnosis
          .replace(/[^a-z0-9\s]/g, ' ')  // Remove special characters
          .replace(/\s+/g, ' ')           // Normalize whitespace
          .trim();
        
        for (const [category, conditions] of Object.entries(MEDSURG_CONDITIONS)) {
          for (const [conditionName, conditionData] of Object.entries(conditions)) {
            let score = 0;
            const keywords = keywordMap[conditionName] || [];
            
            // Check each keyword for matches
            for (const keyword of keywords) {
              const cleanKeyword = keyword.toLowerCase().trim();
              
              // Exact phrase match (highest weight)
              if (cleanDiagnosis.includes(cleanKeyword)) {
                score += cleanKeyword.length * 3;
              }
              
              // Word boundary match (prevents partial word matches)
              const wordBoundaryRegex = new RegExp(`\\b${cleanKeyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
              if (wordBoundaryRegex.test(cleanDiagnosis)) {
                score += cleanKeyword.length * 2;
              }
            }
            
            // Also check condition name itself
            const conditionWords = conditionName.toLowerCase().replace(/[^a-z\s]/g, '').split(' ');
            for (const word of conditionWords) {
              if (word.length > 3 && cleanDiagnosis.includes(word)) {
                score += word.length;
              }
            }
            
            if (score > bestScore) {
              bestScore = score;
              bestMatch = { category, name: conditionName, ...conditionData };
            }
          }
        }
        
        // Fallback: If no good match found, try to find the closest category
        if (!bestMatch || bestScore < 10) {
          const categoryKeywords = {
            "Cardiovascular": ["heart", "cardiac", "chest pain", "bp", "blood pressure", "rhythm"],
            "Respiratory": ["breath", "lung", "oxygen", "respiratory", "pulmonary", "airway"],
            "Endocrine": ["diabetes", "thyroid", "glucose", "sugar", "insulin", "hormone"],
            "Renal": ["kidney", "renal", "creatinine", "dialysis", "urine", "fluid"],
            "Gastrointestinal": ["gi", "stomach", "liver", "bowel", "abdominal", "nausea", "vomit"],
            "Neurological": ["neuro", "stroke", "brain", "seizure", "mental status", "weakness"],
            "Infection/Sepsis": ["infection", "sepsis", "fever", "wbc", "antibiotic"]
          };
          
          for (const [category, keywords] of Object.entries(categoryKeywords)) {
            for (const keyword of keywords) {
              if (cleanDiagnosis.includes(keyword) && MEDSURG_CONDITIONS[category]) {
                // Pick the first condition in that category as a reasonable default
                const firstCondition = Object.entries(MEDSURG_CONDITIONS[category])[0];
                if (firstCondition && (!bestMatch || bestScore < 5)) {
                  bestMatch = { category, name: firstCondition[0], ...firstCondition[1] };
                  bestScore = 5;
                }
                break;
              }
            }
          }
        }
        
        if (bestMatch) {
          setMatchedCondition(bestMatch);
          // Check if this is a psychiatric patient
          const psychCategories = ["Behavioral/Psychiatric", "Psychiatric"];
          setIsPsychPatient(psychCategories.includes(bestMatch.category) || 
            bestMatch.name.includes("Depression") || 
            bestMatch.name.includes("Bipolar") ||
            bestMatch.name.includes("Schizophrenia") ||
            bestMatch.name.includes("Anxiety") ||
            bestMatch.name.includes("Suicidal") ||
            bestMatch.name.includes("Psychosis") ||
            bestMatch.name.includes("Dementia") ||
            bestMatch.name.includes("Withdrawal"));
          
          // Generate patient-specific lab values based on the matched condition
          const conditionLabData = CONDITION_LAB_VALUES[bestMatch.name];
          if (conditionLabData) {
            setPatientLabValues(conditionLabData);
          } else {
            // If no exact match, generate some default slightly abnormal values
            setPatientLabValues({});
          }
        }
        
        // Load existing care plan
        try {
          const cpResponse = await axios.get(`/care-plans/${sessionId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (cpResponse.data) {
            setCarePlan(cpResponse.data);
          }
        } catch (e) {
          // No existing care plan
        }
        
        // Auto-score for reviewing vital signs
        addScore('assessment', 'complete_vital_signs', 15, 'Reviewed vital signs');
        addScore('assessment', 'review_history', 15, 'Reviewed medical history');
        
      } catch (error) {
        toast.error('Failed to load session');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };
    loadSession();
  }, [sessionId, navigate]);

  // Handle action submission
  const handleActionSubmit = async () => {
    if (!actionInput.trim()) return;
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/action`, {
        action_type: actionType,
        action_details: { description: actionInput }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setResponses(prev => [...prev, {
        action: `${actionType}: ${actionInput}`,
        response: response.data.patient_response
      }]);
      
      // Score based on action type
      if (actionType === 'Assessment') {
        addScore('assessment', 'physical_assessment', 20, 'Performed physical assessment');
      } else if (actionType === 'Communication') {
        addScore('communication', 'active_listening', 20, 'Active patient communication');
      } else if (actionType === 'Education') {
        addScore('communication', 'education_provided', 25, 'Provided patient education');
      }
      
      setActionInput('');
      toast.success('Action recorded');
    } catch (error) {
      toast.error('Failed to submit action');
    } finally {
      setSubmitting(false);
    }
  };

  // Medication administration with 5 Rights
  const handleMedAdministration = () => {
    const { patientVerified, drugVerified, doseVerified, routeVerified, timeVerified, selectedMed } = medAdmin;
    
    if (!selectedMed) {
      toast.error('Please select a medication');
      return;
    }
    
    let pointsEarned = 0;
    
    if (patientVerified) {
      addScore('medication', 'verify_patient', 20, 'Verified right patient');
      pointsEarned += 20;
    }
    if (drugVerified) {
      addScore('medication', 'verify_drug', 20, 'Verified right drug');
      pointsEarned += 20;
    }
    if (doseVerified) {
      addScore('medication', 'verify_dose', 20, 'Verified right dose');
      pointsEarned += 20;
    }
    if (routeVerified) {
      addScore('medication', 'verify_route', 20, 'Verified right route');
      pointsEarned += 20;
    }
    if (timeVerified) {
      addScore('medication', 'verify_time', 20, 'Verified right time');
      pointsEarned += 20;
    }
    
    if (pointsEarned === 100) {
      toast.success('🎉 All 5 Rights verified! Medication administered safely.');
      setResponses(prev => [...prev, {
        action: `Medication Administration: ${selectedMed}`,
        response: `Patient received ${selectedMed} safely. All 5 Rights were verified.`
      }]);
    } else if (pointsEarned > 0) {
      toast.warning(`Medication administered with ${pointsEarned/20} of 5 Rights verified.`);
    } else {
      toast.error('Please verify at least one of the 5 Rights before administering.');
    }
    
    // Reset med admin form
    setMedAdmin({
      selectedMed: '',
      patientVerified: false,
      drugVerified: false,
      doseVerified: false,
      routeVerified: false,
      timeVerified: false
    });
  };

  // Infection control actions
  const handleInfectionControlAction = (action) => {
    const actionMap = {
      'hand_hygiene': { points: 30, desc: 'Performed hand hygiene' },
      'ppe_donning': { points: 20, desc: 'Donned appropriate PPE' },
      'sterile_technique': { points: 25, desc: 'Maintained sterile technique' },
      'isolation_precautions': { points: 25, desc: 'Followed isolation precautions' }
    };
    
    if (actionMap[action]) {
      addScore('infection', action, actionMap[action].points, actionMap[action].desc);
    }
  };

  // Communication actions
  const handleCommunicationAction = (action) => {
    const actionMap = {
      'introduce_self': { points: 15, desc: 'Introduced self to patient' },
      'explain_procedures': { points: 20, desc: 'Explained procedures' },
      'addressed_concerns': { points: 20, desc: 'Addressed patient concerns' }
    };
    
    if (actionMap[action]) {
      addScore('communication', action, actionMap[action].points, actionMap[action].desc);
    }
  };

  // Documentation actions
  const handleDocumentationAction = (action) => {
    const actionMap = {
      'timely_documentation': { points: 25, desc: 'Documented in timely manner' },
      'accurate_charting': { points: 25, desc: 'Accurate charting completed' },
      'nursing_notes': { points: 25, desc: 'Written nursing notes' }
    };
    
    if (actionMap[action]) {
      addScore('documentation', action, actionMap[action].points, actionMap[action].desc);
    }
  };

  // Save care plan
  const saveCarePlan = async () => {
    setSavingCarePlan(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/care-plans/${sessionId}`, carePlan, {
        headers: { Authorization: `Bearer ${token}` }
      });
      addScore('documentation', 'care_plan_updated', 25, 'Updated care plan');
      toast.success('Care plan saved successfully!');
    } catch (error) {
      toast.error('Failed to save care plan');
    } finally {
      setSavingCarePlan(false);
    }
  };

  // Care plan helpers
  const addCarePlanItem = (section) => {
    const templates = {
      nursing_diagnoses: { diagnosis: '', related_to: '', evidence: '' },
      goals: { outcome: '', timeframe: '', indicators: '' },
      interventions: { intervention: '', rationale: '', frequency: '' },
      evaluations: { criteria: '', met: '', notes: '' }
    };
    setCarePlan(prev => ({
      ...prev,
      [section]: [...prev[section], templates[section]]
    }));
  };

  const updateCarePlanField = (section, index, field, value) => {
    setCarePlan(prev => ({
      ...prev,
      [section]: prev[section].map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeCarePlanItem = (section, index) => {
    setCarePlan(prev => ({
      ...prev,
      [section]: prev[section].filter((_, i) => i !== index)
    }));
  };

  // SBAR submission to MD
  const handleSbarSubmit = async () => {
    if (!sbarForm.situation.trim() || !sbarForm.background.trim() || 
        !sbarForm.assessment.trim() || !sbarForm.recommendation.trim()) {
      toast.error('Please complete all SBAR fields');
      return;
    }
    
    setSubmittingSbar(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/sbar`, sbarForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Add the response to our history
      setMdResponses(prev => [...prev, {
        sbar: { ...sbarForm },
        response: response.data,
        timestamp: new Date().toISOString()
      }]);
      
      // Add orders to the orders list
      if (response.data.orders && response.data.orders.length > 0) {
        setMdOrders(prev => [...prev, ...response.data.orders]);
      }
      
      // Score for communication
      addScore('communication', 'explain_procedures', 20, 'Communicated with physician via SBAR');
      
      toast.success('Physician orders received!');
      
      // Clear the form
      setSbarForm({
        situation: '',
        background: '',
        assessment: '',
        recommendation: ''
      });
    } catch (error) {
      toast.error('Failed to submit SBAR report');
    } finally {
      setSubmittingSbar(false);
    }
  };

  // Auto-fill SBAR based on patient condition
  const autoFillSbar = () => {
    if (!patient || !matchedCondition) return;
    
    const vitals = patient.vital_signs || {};
    const vitalsStr = Object.entries(vitals).map(([k, v]) => `${k}: ${v}`).join(', ');
    
    setSbarForm({
      situation: `I'm calling about ${patient.name}, a ${patient.age} year old ${patient.gender} admitted with ${patient.diagnosis}. I'm concerned because ${matchedCondition?.clinicalManifestations?.[0] || 'the patient\'s condition is changing'}.`,
      background: `Patient history includes: ${patient.medical_history?.join(', ') || 'see chart'}. Current medications: ${patient.medications?.join(', ') || 'see MAR'}. Current vitals: ${vitalsStr}.`,
      assessment: `My assessment is that the patient is ${vitals.heart_rate > 100 ? 'tachycardic' : vitals.heart_rate < 60 ? 'bradycardic' : 'hemodynamically stable'}. ${patientLabValues && Object.keys(patientLabValues).length > 0 ? 'Abnormal labs include: ' + Object.entries(patientLabValues).filter(([_, v]) => v.status !== 'normal').map(([k, v]) => `${k}: ${v.value}`).join(', ') : ''}`,
      recommendation: `I recommend ${matchedCondition?.nursingInterventions?.[0] || 'further evaluation'}. Would you like to order any medications or additional tests?`
    });
    toast.success('SBAR auto-filled based on patient data');
  };

  // Execute an order
  const handleExecuteOrder = async (order, orderIndex) => {
    const orderId = `${order.order}-${orderIndex}`;
    
    // Check if already executed
    if (executedOrders.includes(orderId)) {
      toast.info('This order has already been executed');
      return;
    }
    
    setExecutingOrderId(orderId);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/execute-order`, {
        order: order,
        current_vitals: currentVitals || patient.vital_signs
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Mark order as executed
      setExecutedOrders(prev => [...prev, orderId]);
      
      // Add patient update to history
      setPatientUpdates(prev => [...prev, {
        order: order,
        response: response.data,
        timestamp: new Date().toISOString()
      }]);
      
      // Update vitals if provided
      if (response.data.updated_vitals) {
        setCurrentVitals(response.data.updated_vitals);
      }
      
      // Score for medication administration if it's a medication order
      if (order.type === 'medication') {
        addScore('medication', `execute_${orderId}`, 10, `Administered ${order.order}`);
      }
      
      // Add to responses for display
      setResponses(prev => [...prev, {
        action: `Executed Order: ${order.order}`,
        response: response.data.patient_response
      }]);
      
      toast.success(`Order executed: ${order.order}`);
      
    } catch (error) {
      toast.error('Failed to execute order');
      console.error(error);
    } finally {
      setExecutingOrderId(null);
    }
  };

  // Initialize current vitals from patient on load
  useEffect(() => {
    if (patient && !currentVitals) {
      setCurrentVitals(patient.vital_signs);
    }
  }, [patient, currentVitals]);

  // Time Progression Handler
  const handleTimeProgression = async (minutes) => {
    setAdvancingTime(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/advance-time`, {
        minutes_to_advance: minutes,
        current_vitals: currentVitals || patient.vital_signs,
        executed_orders: executedOrders.map(id => {
          const parts = id.split('-');
          const index = parseInt(parts.pop());
          return mdOrders[index];
        }).filter(Boolean),
        current_simulation_time: simulationTime
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update simulation time
      setSimulationTime(prev => prev + minutes);
      
      // Update vitals
      if (response.data.updated_vitals) {
        setCurrentVitals(response.data.updated_vitals);
      }
      
      // Add time event to history
      setTimeEvents(prev => [...prev, {
        minutes_advanced: minutes,
        total_time: simulationTime + minutes,
        events: response.data.events,
        patient_status: response.data.patient_status,
        timestamp: new Date().toISOString()
      }]);
      
      // Add to patient updates
      setPatientUpdates(prev => [...prev, {
        order: { order: `Time Advanced: +${minutes} minutes` },
        response: {
          patient_response: response.data.patient_status,
          nurse_observations: response.data.events.join(' ')
        },
        timestamp: new Date().toISOString()
      }]);
      
      // Score for monitoring
      addScore('assessment', `time_check_${simulationTime + minutes}`, 5, 'Performed time-based reassessment');
      
      toast.success(`Time advanced ${minutes} minutes`);
      
    } catch (error) {
      toast.error('Failed to advance time');
      console.error(error);
    } finally {
      setAdvancingTime(false);
    }
  };

  // Format simulation time display
  const formatSimulationTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  // Add behavior note
  const addBehaviorNote = async () => {
    if (!currentBehaviorNote.behavior_observed.trim()) {
      toast.error('Please describe the behavior observed');
      return;
    }
    
    const newNote = {
      ...currentBehaviorNote,
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      simulation_time: formatSimulationTime(simulationTime)
    };
    
    setBehaviorNotes(prev => [...prev, newNote]);
    
    // Score for documentation
    addScore('documentation', `behavior_note_${newNote.id}`, 10, 'Documented behavioral observation');
    
    // Score the note using AI
    setScoringNote(true);
    setLastNoteScore(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/score-behavior-note`, {
        behavior_observed: currentBehaviorNote.behavior_observed,
        mood_affect: currentBehaviorNote.mood_affect,
        safety_concerns: currentBehaviorNote.safety_concerns,
        patient_response: currentBehaviorNote.patient_response,
        nurse_intervention: currentBehaviorNote.nurse_intervention,
        outcome: currentBehaviorNote.outcome,
        simulation_time: formatSimulationTime(simulationTime)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const scoreResult = response.data;
      setLastNoteScore(scoreResult);
      
      // Update the note with its score
      setBehaviorNotes(prev => prev.map(note => 
        note.id === newNote.id 
          ? { ...note, score: scoreResult.total_score, grade: scoreResult.grade, feedback: scoreResult }
          : note
      ));
      
      // Add bonus points based on score quality
      if (scoreResult.total_score >= 80) {
        addScore('documentation', `behavior_quality_${newNote.id}`, 15, 'High-quality behavioral documentation');
      } else if (scoreResult.total_score >= 60) {
        addScore('documentation', `behavior_quality_${newNote.id}`, 8, 'Good behavioral documentation');
      }
      
      toast.success(`Note scored: ${scoreResult.total_score}/100 (${scoreResult.grade})`, { duration: 4000 });
    } catch (error) {
      console.error('Error scoring note:', error);
      toast.error('Note saved but scoring failed');
    } finally {
      setScoringNote(false);
    }
    
    // Reset form
    setCurrentBehaviorNote({
      timestamp: '',
      behavior_observed: '',
      patient_response: '',
      nurse_intervention: '',
      outcome: '',
      mood_affect: '',
      safety_concerns: ''
    });
  };

  // Delete behavior note
  const deleteBehaviorNote = (noteId) => {
    setBehaviorNotes(prev => prev.filter(note => note.id !== noteId));
  };

  // Submit and score comprehensive nursing note
  const submitNursingNote = async () => {
    // Validate required fields
    if (!nursingNote.assessment_findings.trim()) {
      toast.error('Please document your assessment findings');
      return;
    }
    if (!nursingNote.nursing_diagnoses.trim()) {
      toast.error('Please identify nursing diagnoses');
      return;
    }
    if (!nursingNote.interventions_performed.trim()) {
      toast.error('Please document interventions performed');
      return;
    }
    
    setScoringNursingNote(true);
    setNursingNoteScore(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/sessions/${sessionId}/score-nursing-note`, {
        ...nursingNote,
        simulation_time: formatSimulationTime(simulationTime)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const scoreResult = response.data;
      setNursingNoteScore(scoreResult);
      
      // Add to history
      const noteEntry = {
        id: Date.now(),
        timestamp: new Date().toLocaleString(),
        simulation_time: formatSimulationTime(simulationTime),
        note: { ...nursingNote },
        score: scoreResult
      };
      setNursingNoteHistory(prev => [...prev, noteEntry]);
      
      // Add points based on NCLEX result
      if (scoreResult.nclex_result === 'PASS') {
        addScore('documentation', `nursing_note_pass_${noteEntry.id}`, 25, 'NCLEX-passing nursing documentation');
        addScore('assessment', `comprehensive_assessment_${noteEntry.id}`, 15, 'Comprehensive patient assessment');
        addScore('interventions', `documented_interventions_${noteEntry.id}`, 15, 'Evidence-based interventions documented');
      } else if (scoreResult.nclex_result === 'BORDERLINE') {
        addScore('documentation', `nursing_note_borderline_${noteEntry.id}`, 15, 'Borderline nursing documentation');
        addScore('assessment', `basic_assessment_${noteEntry.id}`, 8, 'Basic patient assessment');
      } else {
        addScore('documentation', `nursing_note_attempt_${noteEntry.id}`, 5, 'Nursing note attempted');
      }
      
      toast.success(`Nursing Note Scored: ${scoreResult.total_score}/100 - NCLEX: ${scoreResult.nclex_result}`, { duration: 5000 });
      
    } catch (error) {
      console.error('Error scoring nursing note:', error);
      toast.error('Failed to score nursing note. Please try again.');
    } finally {
      setScoringNursingNote(false);
    }
  };

  // Clear nursing note form
  const clearNursingNote = () => {
    setNursingNote({
      assessment_findings: '',
      vital_signs_interpretation: '',
      nursing_diagnoses: '',
      interventions_performed: '',
      patient_response: '',
      clinical_reasoning: '',
      plan_of_care: '',
      patient_education: ''
    });
    setNursingNoteScore(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-teal-50">
        <div className="text-center">
          <Stethoscope className="w-12 h-12 text-primary animate-pulse mx-auto mb-4" />
          <p className="text-lg font-medium">Loading simulation...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Patient data not found</p>
      </div>
    );
  }

  const overallScore = calculateOverallScore();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-teal-50">
      {/* Header with Score */}
      <div className="bg-white shadow-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Dashboard
              </Button>
              <div className="border-l border-slate-300 h-8"></div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">{patient.name}</h1>
                <p className="text-sm text-slate-600">{patient.age}yo {patient.gender} • {patient.diagnosis}</p>
              </div>
            </div>
            
            {/* Overall Score Display */}
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-slate-600">Overall Performance</p>
                <p className={`text-2xl font-bold ${overallScore >= 80 ? 'text-green-600' : overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {overallScore}%
                </p>
              </div>
              <div className="w-16 h-16 relative">
                <svg className="w-16 h-16 transform -rotate-90">
                  <circle cx="32" cy="32" r="28" stroke="#e2e8f0" strokeWidth="6" fill="none" />
                  <circle 
                    cx="32" cy="32" r="28" 
                    stroke={overallScore >= 80 ? '#22c55e' : overallScore >= 60 ? '#eab308' : '#ef4444'}
                    strokeWidth="6" 
                    fill="none"
                    strokeDasharray={`${overallScore * 1.76} 176`}
                    strokeLinecap="round"
                  />
                </svg>
                <Award className={`w-6 h-6 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${overallScore >= 80 ? 'text-green-600' : overallScore >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Main Navigation Tabs */}
        <Tabs value={activeMainTab} onValueChange={setActiveMainTab} className="w-full">
          <TabsList className="grid w-full grid-cols-9 mb-6 bg-white shadow-sm">
            <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Activity className="w-4 h-4" />
              <span className="hidden sm:inline">Overview</span>
            </TabsTrigger>
            <TabsTrigger value="pathology" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Brain className="w-4 h-4" />
              <span className="hidden sm:inline">Pathology</span>
            </TabsTrigger>
            <TabsTrigger value="medications" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Pill className="w-4 h-4" />
              <span className="hidden sm:inline">Medications</span>
            </TabsTrigger>
            <TabsTrigger value="diagnostics" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <TestTube className="w-4 h-4" />
              <span className="hidden sm:inline">Diagnostics</span>
            </TabsTrigger>
            <TabsTrigger value="mdorders" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">MD Orders</span>
            </TabsTrigger>
            <TabsTrigger value="behavior" className={`gap-2 data-[state=active]:bg-purple-50 data-[state=active]:text-purple-700 ${isPsychPatient ? 'bg-purple-100' : ''}`}>
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">Behavior</span>
            </TabsTrigger>
            <TabsTrigger value="assessment" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <ClipboardCheck className="w-4 h-4" />
              <span className="hidden sm:inline">Assessment</span>
            </TabsTrigger>
            <TabsTrigger value="careplan" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Care Plan</span>
            </TabsTrigger>
            <TabsTrigger value="nursingnote" className="gap-2 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-700">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Nursing Note</span>
            </TabsTrigger>
            <TabsTrigger value="report" className="gap-2 data-[state=active]:bg-teal-50 data-[state=active]:text-teal-700">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Report</span>
            </TabsTrigger>
          </TabsList>

          {/* ============================================ */}
          {/* OVERVIEW TAB */}
          {/* ============================================ */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Patient Info & Vitals */}
              <div className="lg:col-span-2 space-y-6">
                {/* Vitals Monitor */}
                <div className="vitals-monitor rounded-lg p-8">
                  <h3 className="text-center text-lg font-bold mb-6">VITAL SIGNS</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                    {Object.entries(patient.vital_signs).map(([key, value]) => (
                      <div key={key} className="flex flex-col items-center justify-center p-4">
                        <div className="vital-label text-sm uppercase tracking-wider mb-2">{key}</div>
                        <div className="vital-value text-2xl font-bold tracking-wide">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Patient Interaction */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Patient Interaction
                  </h3>
                  <div className="space-y-4">
                    <Select value={actionType} onValueChange={setActionType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Assessment">Assessment</SelectItem>
                        <SelectItem value="Communication">Communication</SelectItem>
                        <SelectItem value="Intervention">Intervention</SelectItem>
                        <SelectItem value="Education">Patient Education</SelectItem>
                        <SelectItem value="Safety">Safety Check</SelectItem>
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={actionInput}
                      onChange={(e) => setActionInput(e.target.value)}
                      placeholder="Describe your action or question to the patient..."
                      className="min-h-[100px]"
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={handleActionSubmit} 
                        disabled={submitting || !actionInput.trim()}
                        className="flex-1 bg-[#0F766E] hover:bg-[#0d6960]"
                      >
                        {submitting ? 'Processing...' : 'Submit Action'}
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={() => handleCommunicationAction('introduce_self')}
                      >
                        Introduce Self
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Responses */}
                {responses.length > 0 && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="text-lg font-semibold mb-4">Patient Responses</h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {responses.map((item, index) => (
                        <div key={index} className="border-l-4 border-primary pl-4 py-2">
                          <p className="text-sm font-medium text-slate-500 mb-1">Your Action:</p>
                          <p className="mb-2 text-slate-800">{item.action}</p>
                          <p className="text-sm font-medium text-primary mb-1">Patient Response:</p>
                          <p className="italic text-slate-700">{item.response}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Quick Actions & Scores */}
              <div className="space-y-6">
                {/* Category Scores */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-primary" />
                    Performance by Category
                  </h3>
                  <div className="space-y-4">
                    {Object.entries(GRADING_CRITERIA).map(([key, criteria]) => {
                      const score = getCategoryScore(key);
                      const Icon = criteria.icon;
                      return (
                        <div key={key}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm flex items-center gap-2">
                              <Icon className="w-4 h-4 text-slate-500" />
                              {criteria.name}
                            </span>
                            <span className={`text-sm font-bold ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-slate-400'}`}>
                              {score}%
                            </span>
                          </div>
                          <Progress value={score} className="h-2" />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-primary" />
                    Infection Control
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleInfectionControlAction('hand_hygiene')}
                      disabled={scores.infection.actions.includes('hand_hygiene')}
                      className="text-xs"
                    >
                      {scores.infection.actions.includes('hand_hygiene') ? '✓ ' : ''}Hand Hygiene
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleInfectionControlAction('ppe_donning')}
                      disabled={scores.infection.actions.includes('ppe_donning')}
                      className="text-xs"
                    >
                      {scores.infection.actions.includes('ppe_donning') ? '✓ ' : ''}Don PPE
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleInfectionControlAction('sterile_technique')}
                      disabled={scores.infection.actions.includes('sterile_technique')}
                      className="text-xs"
                    >
                      {scores.infection.actions.includes('sterile_technique') ? '✓ ' : ''}Sterile Tech
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleInfectionControlAction('isolation_precautions')}
                      disabled={scores.infection.actions.includes('isolation_precautions')}
                      className="text-xs"
                    >
                      {scores.infection.actions.includes('isolation_precautions') ? '✓ ' : ''}Isolation
                    </Button>
                  </div>
                </div>

                {/* Documentation Actions */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    Documentation
                  </h3>
                  <div className="space-y-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleDocumentationAction('timely_documentation')}
                      disabled={scores.documentation.actions.includes('timely_documentation')}
                    >
                      {scores.documentation.actions.includes('timely_documentation') ? <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> : <Clock className="w-4 h-4 mr-2" />}
                      Timely Documentation
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleDocumentationAction('accurate_charting')}
                      disabled={scores.documentation.actions.includes('accurate_charting')}
                    >
                      {scores.documentation.actions.includes('accurate_charting') ? <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> : <Target className="w-4 h-4 mr-2" />}
                      Accurate Charting
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => handleDocumentationAction('nursing_notes')}
                      disabled={scores.documentation.actions.includes('nursing_notes')}
                    >
                      {scores.documentation.actions.includes('nursing_notes') ? <CheckCircle2 className="w-4 h-4 mr-2 text-green-500" /> : <FileText className="w-4 h-4 mr-2" />}
                      Write Nursing Notes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* PATHOLOGY TAB */}
          {/* ============================================ */}
          <TabsContent value="pathology">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {matchedCondition ? (
                  <>
                    <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                      <div className="flex items-center gap-3 mb-4">
                        <Brain className="w-8 h-8 text-primary" />
                        <div>
                          <h2 className="text-xl font-bold text-slate-800">{matchedCondition.name}</h2>
                          <p className="text-sm text-slate-500">{matchedCondition.category}</p>
                        </div>
                      </div>
                      
                      <div className="space-y-6">
                        <div>
                          <h3 className="font-semibold text-primary mb-2 flex items-center gap-2">
                            <BookOpen className="w-4 h-4" />
                            Pathophysiology
                          </h3>
                          <p className="text-slate-700 leading-relaxed">{matchedCondition.pathophysiology}</p>
                        </div>
                        
                        <div>
                          <h3 className="font-semibold text-primary mb-2">Etiology/Risk Factors</h3>
                          <ul className="list-disc list-inside space-y-1">
                            {matchedCondition.etiology.map((item, i) => (
                              <li key={i} className="text-slate-700">{item}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div>
                          <h3 className="font-semibold text-primary mb-2 flex items-center gap-2">
                            <AlertTriangle className="w-4 h-4" />
                            Clinical Manifestations
                          </h3>
                          <div className="grid grid-cols-2 gap-2">
                            {matchedCondition.clinicalManifestations.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm">
                                <span className="w-2 h-2 rounded-full bg-primary"></span>
                                <span className="text-slate-700">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                      <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                        <Stethoscope className="w-5 h-5" />
                        Priority Nursing Interventions
                      </h3>
                      <div className="space-y-2">
                        {matchedCondition.nursingInterventions.map((item, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-teal-50 rounded-lg">
                            <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold">{i + 1}</span>
                            <span className="text-slate-700">{item}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h2 className="text-xl font-bold text-slate-800 mb-4">Med-Surg Conditions Reference</h2>
                    <p className="text-slate-600 mb-4">Select a condition to view detailed pathology information:</p>
                    <div className="space-y-4">
                      {Object.entries(MEDSURG_CONDITIONS).map(([category, conditions]) => (
                        <div key={category}>
                          <h3 className="font-semibold text-primary mb-2">{category}</h3>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.keys(conditions).map(condition => (
                              <Button 
                                key={condition}
                                variant="outline"
                                size="sm"
                                className="justify-start text-left"
                                onClick={() => setMatchedCondition({ category, name: condition, ...conditions[condition] })}
                              >
                                {condition}
                              </Button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {matchedCondition && (
                  <>
                    <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                      <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                        <TestTube className="w-5 h-5" />
                        Key Diagnostics
                      </h3>
                      <ul className="space-y-2">
                        {matchedCondition.diagnostics.map((item, i) => (
                          <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                      <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                        <Pill className="w-5 h-5" />
                        Common Medications
                      </h3>
                      <ul className="space-y-2">
                        {matchedCondition.medications.map((item, i) => (
                          <li key={i} className="text-sm text-slate-700 p-2 bg-slate-50 rounded">{item}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}

                {/* Score for this section */}
                <div className="medical-card p-6 bg-gradient-to-br from-teal-50 to-cyan-50">
                  <h3 className="font-semibold mb-2">Pathology Knowledge</h3>
                  <p className="text-sm text-slate-600 mb-3">Review condition information to improve assessment accuracy</p>
                  <div className="text-center">
                    <span className="text-3xl font-bold text-primary">{getCategoryScore('assessment')}%</span>
                    <p className="text-xs text-slate-500">Assessment Score</p>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* MEDICATIONS TAB - 5 Rights Practice */}
          {/* ============================================ */}
          <TabsContent value="medications">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                {/* 5 Rights Medication Administration */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <Pill className="w-6 h-6 text-primary" />
                    Medication Administration - 5 Rights
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">Practice safe medication administration by verifying all 5 Rights</p>
                  
                  <div className="space-y-6">
                    {/* Medication Selection */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Select Medication to Administer</label>
                      <Select value={medAdmin.selectedMed} onValueChange={(val) => setMedAdmin(prev => ({ ...prev, selectedMed: val }))}>
                        <SelectTrigger>
                          <SelectValue placeholder="Choose medication..." />
                        </SelectTrigger>
                        <SelectContent>
                          {patient.current_medications.map((med, i) => (
                            <SelectItem key={i} value={med}>{med}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* 5 Rights Checklist */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {[
                        { key: 'patientVerified', label: '1. Right Patient', desc: 'Verify using 2 identifiers (name + DOB)' },
                        { key: 'drugVerified', label: '2. Right Drug', desc: 'Check medication name against MAR' },
                        { key: 'doseVerified', label: '3. Right Dose', desc: 'Verify dose calculation and amount' },
                        { key: 'routeVerified', label: '4. Right Route', desc: 'Confirm administration route (PO, IV, etc.)' },
                        { key: 'timeVerified', label: '5. Right Time', desc: 'Check scheduled time (±30 min window)' }
                      ].map(item => (
                        <div 
                          key={item.key}
                          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                            medAdmin[item.key] 
                              ? 'border-green-500 bg-green-50' 
                              : 'border-slate-200 hover:border-primary'
                          }`}
                          onClick={() => setMedAdmin(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                        >
                          <div className="flex items-center gap-3">
                            {medAdmin[item.key] ? (
                              <CheckCircle2 className="w-6 h-6 text-green-500" />
                            ) : (
                              <div className="w-6 h-6 rounded-full border-2 border-slate-300"></div>
                            )}
                            <div>
                              <p className="font-medium text-slate-800">{item.label}</p>
                              <p className="text-xs text-slate-500">{item.desc}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <Button 
                      onClick={handleMedAdministration}
                      className="w-full bg-[#0F766E] hover:bg-[#0d6960]"
                      disabled={!medAdmin.selectedMed}
                    >
                      <Pill className="w-4 h-4 mr-2" />
                      Administer Medication
                    </Button>
                  </div>
                </div>

                {/* Medication Reference */}
                {medAdmin.selectedMed && MEDICATION_DATABASE[medAdmin.selectedMed.split(' ')[0]] && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-4">Medication Reference</h3>
                    {(() => {
                      const medName = medAdmin.selectedMed.split(' ')[0];
                      const medInfo = MEDICATION_DATABASE[medName];
                      if (!medInfo) return null;
                      return (
                        <div className="space-y-3 text-sm">
                          <p><strong>Class:</strong> {medInfo.class}</p>
                          <p><strong>Route:</strong> {medInfo.route}</p>
                          <p><strong>Common Doses:</strong> {medInfo.commonDoses.join(', ')}</p>
                          <p><strong>Frequency:</strong> {medInfo.frequency}</p>
                          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <p className="font-medium text-yellow-800 flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4" />
                              Nursing Considerations
                            </p>
                            <p className="text-yellow-700 mt-1">{medInfo.considerations}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Current Medications */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-4">Patient's Medications</h3>
                  <ul className="space-y-2">
                    {patient.current_medications.map((med, i) => (
                      <li key={i} className="p-2 bg-slate-50 rounded text-sm">{med}</li>
                    ))}
                  </ul>
                </div>

                {/* Medication Safety Score */}
                <div className="medical-card p-6 bg-gradient-to-br from-teal-50 to-cyan-50">
                  <h3 className="font-semibold mb-2">Medication Safety Score</h3>
                  <div className="text-center my-4">
                    <span className="text-4xl font-bold text-primary">{getCategoryScore('medication')}%</span>
                  </div>
                  <Progress value={getCategoryScore('medication')} className="h-3 mb-4" />
                  <div className="space-y-2">
                    {GRADING_CRITERIA.medication.items.map(item => (
                      <div key={item.action} className="flex items-center gap-2 text-sm">
                        {scores.medication.actions.includes(item.action) ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                        <span className={scores.medication.actions.includes(item.action) ? 'text-green-700' : 'text-slate-500'}>
                          {item.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* DIAGNOSTICS TAB */}
          {/* ============================================ */}
          <TabsContent value="diagnostics">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h2 className="text-xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <TestTube className="w-6 h-6 text-primary" />
                    Lab Values & Diagnostics Reference
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">Review and interpret diagnostic results for your patient</p>
                  
                  <Tabs defaultValue="cbc" className="w-full">
                    <TabsList className="grid w-full grid-cols-5 mb-4">
                      <TabsTrigger value="cbc">CBC</TabsTrigger>
                      <TabsTrigger value="bmp">BMP</TabsTrigger>
                      <TabsTrigger value="cardiac">Cardiac</TabsTrigger>
                      <TabsTrigger value="coag">Coag</TabsTrigger>
                      <TabsTrigger value="abg">ABG</TabsTrigger>
                    </TabsList>

                    {Object.entries(LAB_REFERENCE).map(([panel, labs]) => (
                      <TabsContent key={panel} value={panel.toLowerCase()}>
                        <div className="space-y-3">
                          {Object.entries(labs).map(([labName, labData]) => {
                            // Check if this lab has a patient-specific abnormal value
                            const patientValue = patientLabValues[labName];
                            const hasAbnormal = patientValue !== undefined;
                            const isHigh = hasAbnormal && (patientValue.status === 'high' || patientValue.status === 'critical-high');
                            const isLow = hasAbnormal && (patientValue.status === 'low' || patientValue.status === 'critical-low');
                            const isCritical = hasAbnormal && (patientValue.status === 'critical-high' || patientValue.status === 'critical-low');
                            
                            return (
                              <div 
                                key={labName} 
                                className={`p-4 border-2 rounded-lg transition-all ${
                                  isCritical ? 'border-red-500 bg-red-50' : 
                                  hasAbnormal && patientValue.status !== 'normal' ? 'border-amber-400 bg-amber-50' : 
                                  'border-slate-200'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold text-slate-800">{labName}</h4>
                                    {isCritical && (
                                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 bg-red-600 text-white rounded-full animate-pulse">
                                        <AlertTriangle className="w-3 h-3" /> CRITICAL
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-sm px-3 py-1 bg-green-100 text-green-700 rounded-full">
                                    Normal: {labData.normal}
                                  </span>
                                </div>
                                
                                {/* Patient's Actual Value */}
                                {hasAbnormal && (
                                  <div className={`mb-3 p-3 rounded-lg ${
                                    isCritical ? 'bg-red-100 border border-red-300' : 
                                    isHigh ? 'bg-orange-100 border border-orange-300' :
                                    isLow ? 'bg-blue-100 border border-blue-300' :
                                    'bg-green-100 border border-green-300'
                                  }`}>
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-slate-700">Patient's Value:</span>
                                      <span className={`text-lg font-bold ${
                                        isCritical ? 'text-red-700' : 
                                        isHigh ? 'text-orange-700' :
                                        isLow ? 'text-blue-700' :
                                        'text-green-700'
                                      }`}>
                                        {patientValue.value} {labData.unit}
                                        {isHigh && <TrendingUp className="inline w-4 h-4 ml-1" />}
                                        {isLow && <TrendingUp className="inline w-4 h-4 ml-1 rotate-180" />}
                                      </span>
                                    </div>
                                    <p className={`text-xs mt-1 ${
                                      isCritical ? 'text-red-600' : 
                                      isHigh ? 'text-orange-600' :
                                      isLow ? 'text-blue-600' :
                                      'text-green-600'
                                    }`}>
                                      {isCritical ? '⚠️ CRITICAL VALUE - Immediate attention required' :
                                       isHigh ? '↑ Above normal range' :
                                       isLow ? '↓ Below normal range' :
                                       '✓ Within normal limits'}
                                    </p>
                                  </div>
                                )}
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-red-600 font-medium flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3" /> High indicates:
                                    </p>
                                    <p className="text-slate-600">{labData.high}</p>
                                  </div>
                                  <div>
                                    <p className="text-blue-600 font-medium flex items-center gap-1">
                                      <TrendingUp className="w-3 h-3 rotate-180" /> Low indicates:
                                    </p>
                                    <p className="text-slate-600">{labData.low}</p>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Patient's Abnormal Labs Summary */}
                {Object.keys(patientLabValues).length > 0 && (
                  <div className="medical-card p-6 border-2 border-red-200" style={{backgroundColor: '#fef2f2'}}>
                    <h3 className="font-semibold text-red-700 mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Patient's Abnormal Labs
                    </h3>
                    <div className="space-y-2">
                      {Object.entries(patientLabValues).map(([labName, labInfo]) => {
                        if (labInfo.status === 'normal') return null;
                        const labRef = Object.values(LAB_REFERENCE).flatMap(panel => 
                          Object.entries(panel).filter(([name]) => name === labName)
                        )[0];
                        const unit = labRef ? labRef[1].unit : '';
                        const isCritical = labInfo.status.includes('critical');
                        
                        return (
                          <div 
                            key={labName} 
                            className={`p-2 rounded flex items-center justify-between ${
                              isCritical ? 'bg-red-200' : 'bg-amber-100'
                            }`}
                          >
                            <span className="text-sm font-medium text-slate-800">{labName}</span>
                            <span className={`text-sm font-bold ${
                              isCritical ? 'text-red-700' : 
                              labInfo.status.includes('high') ? 'text-orange-700' : 'text-blue-700'
                            }`}>
                              {labInfo.value} {unit}
                              {labInfo.status.includes('high') ? ' ↑' : ' ↓'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {matchedCondition && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-4">Expected Diagnostics for {matchedCondition.name}</h3>
                    <ul className="space-y-2">
                      {matchedCondition.diagnostics.map((item, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-start gap-2 p-2 bg-teal-50 rounded">
                          <CheckCircle2 className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="medical-card p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <h3 className="font-semibold mb-2">Diagnostic Interpretation</h3>
                  <p className="text-sm text-slate-600 mb-3">Click on any abnormal finding to score points for identifying critical values</p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => addScore('assessment', 'identify_abnormals', 20, 'Identified abnormal findings')}
                    disabled={scores.assessment.actions.includes('identify_abnormals')}
                  >
                    {scores.assessment.actions.includes('identify_abnormals') ? '✓ ' : ''}Mark Abnormals Identified
                  </Button>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* MD ORDERS TAB */}
          {/* ============================================ */}
          <TabsContent value="mdorders">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - SBAR Form */}
              <div className="lg:col-span-2 space-y-6">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                      <Phone className="w-6 h-6 text-primary" />
                      Call Physician - SBAR Report
                    </h2>
                    <Button variant="outline" size="sm" onClick={autoFillSbar}>
                      Auto-fill from Patient Data
                    </Button>
                  </div>
                  <p className="text-sm text-slate-500 mb-6">
                    Use the SBAR format to communicate patient status to the physician and receive medication orders.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Situation */}
                    <div className="p-4 border-2 border-red-200 rounded-lg bg-red-50">
                      <label className="block text-sm font-bold text-red-700 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center text-xs">S</span>
                        SITUATION - What is happening?
                      </label>
                      <Textarea
                        value={sbarForm.situation}
                        onChange={(e) => setSbarForm(prev => ({ ...prev, situation: e.target.value }))}
                        placeholder="I am calling about [patient name], a [age] year old [gender] admitted with [diagnosis]. I am calling because..."
                        className="min-h-[80px] bg-white"
                      />
                    </div>
                    
                    {/* Background */}
                    <div className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
                      <label className="block text-sm font-bold text-orange-700 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-orange-600 text-white flex items-center justify-center text-xs">B</span>
                        BACKGROUND - What is the clinical background?
                      </label>
                      <Textarea
                        value={sbarForm.background}
                        onChange={(e) => setSbarForm(prev => ({ ...prev, background: e.target.value }))}
                        placeholder="Patient was admitted on [date] for [reason]. Medical history includes... Current medications are... Allergies include..."
                        className="min-h-[80px] bg-white"
                      />
                    </div>
                    
                    {/* Assessment */}
                    <div className="p-4 border-2 border-yellow-200 rounded-lg bg-yellow-50">
                      <label className="block text-sm font-bold text-yellow-700 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-yellow-600 text-white flex items-center justify-center text-xs">A</span>
                        ASSESSMENT - What do I think the problem is?
                      </label>
                      <Textarea
                        value={sbarForm.assessment}
                        onChange={(e) => setSbarForm(prev => ({ ...prev, assessment: e.target.value }))}
                        placeholder="Current vital signs are... My assessment is that the patient is... Labs show..."
                        className="min-h-[80px] bg-white"
                      />
                    </div>
                    
                    {/* Recommendation */}
                    <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
                      <label className="block text-sm font-bold text-green-700 mb-2 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center text-xs">R</span>
                        RECOMMENDATION - What do I want?
                      </label>
                      <Textarea
                        value={sbarForm.recommendation}
                        onChange={(e) => setSbarForm(prev => ({ ...prev, recommendation: e.target.value }))}
                        placeholder="I recommend... Would you like to order...? Do you want me to...?"
                        className="min-h-[80px] bg-white"
                      />
                    </div>
                    
                    <Button 
                      onClick={handleSbarSubmit}
                      disabled={submittingSbar}
                      className="w-full bg-[#0F766E] hover:bg-[#0d6960] flex items-center justify-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      {submittingSbar ? 'Calling Physician...' : 'Submit SBAR to Physician'}
                    </Button>
                  </div>
                </div>
                
                {/* MD Response History */}
                {mdResponses.length > 0 && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      Physician Responses
                    </h3>
                    <div className="space-y-4 max-h-[400px] overflow-y-auto">
                      {mdResponses.map((item, index) => (
                        <div key={index} className="border-l-4 border-blue-500 pl-4 py-3 bg-blue-50 rounded-r-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-semibold text-blue-800">{item.response.physician_name}</span>
                            <span className="text-xs text-slate-500">
                              {new Date(item.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="text-slate-700 italic mb-3">"{item.response.verbal_response}"</p>
                          
                          {item.response.orders && item.response.orders.length > 0 && (
                            <div className="mt-2">
                              <p className="text-sm font-medium text-slate-600 mb-2">Orders:</p>
                              <div className="space-y-2">
                                {item.response.orders.map((order, oi) => (
                                  <div key={oi} className={`p-2 rounded text-sm ${
                                    order.priority === 'stat' ? 'bg-red-100 border-l-4 border-red-500' :
                                    order.priority === 'urgent' ? 'bg-orange-100 border-l-4 border-orange-500' :
                                    'bg-white border-l-4 border-slate-300'
                                  }`}>
                                    <div className="flex items-center justify-between">
                                      <span className="font-medium">{order.order}</span>
                                      <span className={`text-xs px-2 py-0.5 rounded ${
                                        order.priority === 'stat' ? 'bg-red-600 text-white' :
                                        order.priority === 'urgent' ? 'bg-orange-600 text-white' :
                                        'bg-slate-200 text-slate-600'
                                      }`}>
                                        {order.priority?.toUpperCase() || 'ROUTINE'}
                                      </span>
                                    </div>
                                    {order.dose && <p className="text-slate-600">Dose: {order.dose} | Route: {order.route} | Freq: {order.frequency}</p>}
                                    {order.rationale && <p className="text-slate-500 text-xs mt-1">Rationale: {order.rationale}</p>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Active Orders & Quick Reference */}
              <div className="space-y-6">
                {/* Time Progression Panel */}
                <div className="medical-card p-6 border-2 border-indigo-300" style={{backgroundColor: '#eef2ff'}}>
                  <h3 className="font-semibold text-indigo-700 mb-3 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Simulation Time
                  </h3>
                  <div className="text-center mb-4">
                    <span className="text-3xl font-bold text-indigo-800">
                      {formatSimulationTime(simulationTime)}
                    </span>
                    <p className="text-xs text-indigo-600 mt-1">elapsed since simulation start</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs bg-white hover:bg-indigo-100"
                      onClick={() => handleTimeProgression(15)}
                      disabled={advancingTime || executedOrders.length === 0}
                    >
                      +15 min
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs bg-white hover:bg-indigo-100"
                      onClick={() => handleTimeProgression(30)}
                      disabled={advancingTime || executedOrders.length === 0}
                    >
                      +30 min
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-xs bg-white hover:bg-indigo-100"
                      onClick={() => handleTimeProgression(60)}
                      disabled={advancingTime || executedOrders.length === 0}
                    >
                      +1 hour
                    </Button>
                  </div>
                  {advancingTime && (
                    <div className="mt-3 text-center text-sm text-indigo-600 animate-pulse">
                      Calculating patient status changes...
                    </div>
                  )}
                  {executedOrders.length === 0 && (
                    <p className="text-xs text-slate-500 mt-3 text-center">
                      Execute at least one order to advance time
                    </p>
                  )}
                </div>
                
                {/* Updated Vitals Display */}
                {currentVitals && (
                  <div className={`medical-card p-6 border-2 ${patientUpdates.length > 0 ? 'border-green-300' : 'border-slate-200'}`} style={{backgroundColor: patientUpdates.length > 0 ? '#f0fdf4' : 'white'}}>
                    <h3 className={`font-semibold mb-3 flex items-center gap-2 ${patientUpdates.length > 0 ? 'text-green-700' : 'text-primary'}`}>
                      <Activity className="w-5 h-5" />
                      {patientUpdates.length > 0 ? 'Current Vitals (Updated)' : 'Current Vitals'}
                    </h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(currentVitals).map(([key, value]) => (
                        <div key={key} className="flex justify-between p-1">
                          <span className="text-slate-600">{key}:</span>
                          <span className="font-medium text-slate-800">{value}</span>
                        </div>
                      ))}
                    </div>
                    {simulationTime > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                        Last updated at: {formatSimulationTime(simulationTime)} into simulation
                      </div>
                    )}
                  </div>
                )}
                
                {/* Time Events History */}
                {timeEvents.length > 0 && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Clinical Events Timeline
                    </h3>
                    <div className="space-y-3 max-h-[200px] overflow-y-auto">
                      {timeEvents.slice().reverse().map((event, index) => (
                        <div key={index} className="border-l-4 border-indigo-400 pl-3 py-2 bg-indigo-50 rounded-r">
                          <p className="text-xs font-medium text-indigo-700">
                            At {formatSimulationTime(event.total_time)}
                          </p>
                          <ul className="text-xs text-slate-600 mt-1 space-y-1">
                            {event.events.map((evt, ei) => (
                              <li key={ei}>• {evt}</li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Active Orders Summary */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-5 h-5" />
                    Active Orders ({mdOrders.length})
                  </h3>
                  {mdOrders.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No orders received yet. Submit an SBAR report to receive physician orders.</p>
                  ) : (
                    <div className="space-y-2 max-h-[400px] overflow-y-auto">
                      {mdOrders.map((order, index) => {
                        const orderId = `${order.order}-${index}`;
                        const isExecuted = executedOrders.includes(orderId);
                        const isExecuting = executingOrderId === orderId;
                        
                        return (
                          <div key={index} className={`p-3 rounded-lg text-sm transition-all ${
                            isExecuted ? 'bg-green-100 border-2 border-green-400' :
                            order.priority === 'stat' ? 'bg-red-100 border border-red-300' :
                            order.priority === 'urgent' ? 'bg-orange-100 border border-orange-300' :
                            'bg-slate-100 border border-slate-200'
                          }`}>
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1">
                                <div className="font-medium text-slate-800 flex items-center gap-2">
                                  {isExecuted && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                                  {order.order}
                                </div>
                                {order.dose && (
                                  <div className="text-slate-600 text-xs mt-1">
                                    {order.dose} | {order.route} | {order.frequency}
                                  </div>
                                )}
                              </div>
                              {!isExecuted && (
                                <Button
                                  size="sm"
                                  variant={order.priority === 'stat' ? 'destructive' : 'outline'}
                                  className="text-xs px-2 py-1 h-7"
                                  onClick={() => handleExecuteOrder(order, index)}
                                  disabled={isExecuting}
                                >
                                  {isExecuting ? (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3 animate-spin" />
                                      Executing...
                                    </span>
                                  ) : (
                                    'Execute'
                                  )}
                                </Button>
                              )}
                            </div>
                            {isExecuted && (
                              <div className="mt-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                                ✓ Order completed
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Patient Response History */}
                {patientUpdates.length > 0 && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Patient Responses
                    </h3>
                    <div className="space-y-3 max-h-[250px] overflow-y-auto">
                      {patientUpdates.slice().reverse().map((update, index) => (
                        <div key={index} className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-400">
                          <p className="text-xs font-medium text-blue-700 mb-1">After: {update.order.order}</p>
                          <p className="text-sm text-slate-700 italic">"{update.response.patient_response}"</p>
                          {update.response.nurse_observations && (
                            <p className="text-xs text-slate-600 mt-2">
                              <span className="font-medium">Nurse observes:</span> {update.response.nurse_observations}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SBAR Quick Reference */}
                <div className="medical-card p-6 bg-gradient-to-br from-blue-50 to-indigo-50">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    SBAR Quick Reference
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="p-2 bg-red-100 rounded">
                      <span className="font-bold text-red-700">S - Situation:</span>
                      <p className="text-slate-600">Identify yourself, unit, patient, and why you're calling</p>
                    </div>
                    <div className="p-2 bg-orange-100 rounded">
                      <span className="font-bold text-orange-700">B - Background:</span>
                      <p className="text-slate-600">Admission date, diagnosis, pertinent history, meds, allergies</p>
                    </div>
                    <div className="p-2 bg-yellow-100 rounded">
                      <span className="font-bold text-yellow-700">A - Assessment:</span>
                      <p className="text-slate-600">Vital signs, clinical impression, severity</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded">
                      <span className="font-bold text-green-700">R - Recommendation:</span>
                      <p className="text-slate-600">What you need from the physician</p>
                    </div>
                  </div>
                </div>

                {/* Patient Quick Info */}
                {patient && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-3">Patient Quick Info</h3>
                    <div className="space-y-2 text-sm">
                      <p><span className="font-medium">Name:</span> {patient.name}</p>
                      <p><span className="font-medium">Age/Gender:</span> {patient.age}yo {patient.gender}</p>
                      <p><span className="font-medium">Diagnosis:</span> {patient.diagnosis}</p>
                      <p><span className="font-medium">Allergies:</span> {patient.allergies?.join(', ') || 'NKDA'}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* BEHAVIOR NOTES TAB */}
          {/* ============================================ */}
          <TabsContent value="behavior">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Behavior Note Form */}
              <div className="lg:col-span-2 space-y-6">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <MessageSquare className="w-6 h-6 text-purple-600" />
                    Behavior Documentation
                    {isPsychPatient && (
                      <span className="ml-2 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                        Psych Patient
                      </span>
                    )}
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    Document patient behaviors, responses, and nursing interventions. This is essential for psychiatric and behavioral health assessment.
                  </p>
                  
                  <div className="space-y-4">
                    {/* Behavior Observed */}
                    <div className="p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
                      <label className="block text-sm font-bold text-purple-700 mb-2">
                        Behavior Observed *
                      </label>
                      <Textarea
                        value={currentBehaviorNote.behavior_observed}
                        onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, behavior_observed: e.target.value }))}
                        placeholder="Describe the behavior observed (e.g., Patient pacing in hallway, talking to self, refusing medications, expressing hopelessness...)"
                        className="min-h-[80px] bg-white"
                      />
                    </div>
                    
                    {/* Mood/Affect Assessment */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Mood/Affect
                        </label>
                        <select
                          value={currentBehaviorNote.mood_affect}
                          onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, mood_affect: e.target.value }))}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Select mood/affect...</option>
                          <option value="Euthymic">Euthymic (normal)</option>
                          <option value="Depressed">Depressed</option>
                          <option value="Anxious">Anxious</option>
                          <option value="Irritable">Irritable</option>
                          <option value="Euphoric">Euphoric</option>
                          <option value="Flat">Flat affect</option>
                          <option value="Labile">Labile (rapidly changing)</option>
                          <option value="Agitated">Agitated</option>
                          <option value="Fearful">Fearful</option>
                          <option value="Angry">Angry</option>
                        </select>
                      </div>
                      
                      <div className="p-4 border border-slate-200 rounded-lg">
                        <label className="block text-sm font-bold text-slate-700 mb-2">
                          Safety Concerns
                        </label>
                        <select
                          value={currentBehaviorNote.safety_concerns}
                          onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, safety_concerns: e.target.value }))}
                          className="w-full p-2 border rounded-md"
                        >
                          <option value="">Select safety level...</option>
                          <option value="None identified">None identified</option>
                          <option value="Low risk">Low risk - routine monitoring</option>
                          <option value="Moderate risk">Moderate risk - increased monitoring</option>
                          <option value="High risk SI">High risk - suicidal ideation</option>
                          <option value="High risk violence">High risk - potential violence</option>
                          <option value="Elopement risk">Elopement risk</option>
                          <option value="Fall risk">Fall risk</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Patient Response */}
                    <div className="p-4 border border-slate-200 rounded-lg">
                      <label className="block text-sm font-bold text-slate-700 mb-2">
                        Patient Response/Verbalization
                      </label>
                      <Textarea
                        value={currentBehaviorNote.patient_response}
                        onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, patient_response: e.target.value }))}
                        placeholder="Document patient's verbal response, statements, or quotes (e.g., Patient states: 'I don't want to be here'...)"
                        className="min-h-[60px]"
                      />
                    </div>
                    
                    {/* Nursing Intervention */}
                    <div className="p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                      <label className="block text-sm font-bold text-blue-700 mb-2">
                        Nursing Intervention
                      </label>
                      <Textarea
                        value={currentBehaviorNote.nurse_intervention}
                        onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, nurse_intervention: e.target.value }))}
                        placeholder="Document your intervention (e.g., Used therapeutic communication, redirected patient, administered PRN medication, initiated 1:1 observation...)"
                        className="min-h-[60px] bg-white"
                      />
                    </div>
                    
                    {/* Outcome */}
                    <div className="p-4 border-2 border-green-200 rounded-lg bg-green-50">
                      <label className="block text-sm font-bold text-green-700 mb-2">
                        Outcome/Patient Response to Intervention
                      </label>
                      <Textarea
                        value={currentBehaviorNote.outcome}
                        onChange={(e) => setCurrentBehaviorNote(prev => ({ ...prev, outcome: e.target.value }))}
                        placeholder="Document the outcome (e.g., Patient calmed after intervention, agreed to take medication, continued agitation - MD notified...)"
                        className="min-h-[60px] bg-white"
                      />
                    </div>
                    
                    <Button 
                      onClick={addBehaviorNote}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                      disabled={scoringNote}
                    >
                      {scoringNote ? (
                        <>
                          <Activity className="w-4 h-4 mr-2 animate-spin" />
                          Scoring Note...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add & Score Behavior Note
                        </>
                      )}
                    </Button>
                    
                    {/* Last Score Feedback Panel */}
                    {lastNoteScore && (
                      <div className="mt-4 p-4 border-2 border-purple-300 rounded-lg bg-gradient-to-br from-purple-50 to-white">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-bold text-purple-800 flex items-center gap-2">
                            <Award className="w-5 h-5" />
                            Documentation Score
                          </h4>
                          <div className={`text-2xl font-bold px-3 py-1 rounded-lg ${
                            lastNoteScore.grade === 'A' ? 'bg-green-100 text-green-700' :
                            lastNoteScore.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                            lastNoteScore.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                            lastNoteScore.grade === 'D' ? 'bg-orange-100 text-orange-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {lastNoteScore.total_score}/100 ({lastNoteScore.grade})
                          </div>
                        </div>
                        
                        <p className="text-sm text-slate-600 mb-3">{lastNoteScore.overall_feedback}</p>
                        
                        {/* Category Scores */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                          {lastNoteScore.category_scores && Object.entries(lastNoteScore.category_scores).map(([key, value]) => (
                            <div key={key} className="flex items-center justify-between text-xs bg-white p-2 rounded border">
                              <span className="font-medium text-slate-600 capitalize">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className={`font-bold ${
                                value.score >= 16 ? 'text-green-600' :
                                value.score >= 10 ? 'text-yellow-600' :
                                'text-red-600'
                              }`}>
                                {value.score}/{key === 'nursing_intervention' ? 25 : key === 'safety_assessment' ? 15 : 20}
                              </span>
                            </div>
                          ))}
                        </div>
                        
                        <div className="grid grid-cols-2 gap-3 text-xs">
                          {lastNoteScore.strengths && lastNoteScore.strengths.length > 0 && (
                            <div className="bg-green-50 p-2 rounded">
                              <span className="font-bold text-green-700 block mb-1">Strengths:</span>
                              <ul className="text-green-600 space-y-0.5">
                                {lastNoteScore.strengths.map((s, i) => (
                                  <li key={i}>• {s}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {lastNoteScore.areas_for_improvement && lastNoteScore.areas_for_improvement.length > 0 && (
                            <div className="bg-amber-50 p-2 rounded">
                              <span className="font-bold text-amber-700 block mb-1">To Improve:</span>
                              <ul className="text-amber-600 space-y-0.5">
                                {lastNoteScore.areas_for_improvement.map((a, i) => (
                                  <li key={i}>• {a}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Behavior Notes History */}
                {behaviorNotes.length > 0 && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
                      <FileText className="w-5 h-5 text-purple-600" />
                      Documented Behavior Notes ({behaviorNotes.length})
                    </h3>
                    <div className="space-y-4 max-h-[500px] overflow-y-auto">
                      {behaviorNotes.slice().reverse().map((note) => (
                        <div key={note.id} className="border-l-4 border-purple-500 pl-4 py-3 bg-slate-50 rounded-r-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-500">
                                {note.timestamp} | Sim Time: {note.simulation_time}
                              </span>
                              {note.score !== undefined && (
                                <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                                  note.grade === 'A' ? 'bg-green-100 text-green-700' :
                                  note.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                  note.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                                  note.grade === 'D' ? 'bg-orange-100 text-orange-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  Score: {note.score}/100 ({note.grade})
                                </span>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteBehaviorNote(note.id)}
                              className="text-red-500 hover:text-red-700 h-6 px-2"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          
                          <div className="space-y-2 text-sm">
                            <div>
                              <span className="font-medium text-purple-700">Behavior: </span>
                              <span className="text-slate-700">{note.behavior_observed}</span>
                            </div>
                            
                            {note.mood_affect && (
                              <div>
                                <span className="font-medium text-slate-600">Mood/Affect: </span>
                                <span className={`px-2 py-0.5 rounded text-xs ${
                                  note.mood_affect === 'Euthymic' ? 'bg-green-100 text-green-700' :
                                  note.mood_affect === 'Agitated' || note.mood_affect === 'Angry' ? 'bg-red-100 text-red-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {note.mood_affect}
                                </span>
                              </div>
                            )}
                            
                            {note.safety_concerns && note.safety_concerns !== 'None identified' && (
                              <div>
                                <span className="font-medium text-red-600">⚠ Safety: </span>
                                <span className="text-red-700">{note.safety_concerns}</span>
                              </div>
                            )}
                            
                            {note.patient_response && (
                              <div>
                                <span className="font-medium text-slate-600">Patient Response: </span>
                                <span className="text-slate-700 italic">"{note.patient_response}"</span>
                              </div>
                            )}
                            
                            {note.nurse_intervention && (
                              <div>
                                <span className="font-medium text-blue-600">Intervention: </span>
                                <span className="text-slate-700">{note.nurse_intervention}</span>
                              </div>
                            )}
                            
                            {note.outcome && (
                              <div>
                                <span className="font-medium text-green-600">Outcome: </span>
                                <span className="text-slate-700">{note.outcome}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - Quick Reference */}
              <div className="space-y-6">
                {/* Behavioral Assessment Quick Guide */}
                <div className="medical-card p-6 bg-gradient-to-br from-purple-50 to-indigo-50">
                  <h3 className="font-semibold text-purple-800 mb-3 flex items-center gap-2">
                    <BookOpen className="w-5 h-5" />
                    Behavioral Assessment Guide
                  </h3>
                  <div className="space-y-3 text-sm">
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Appearance:</span>
                      <p className="text-slate-600">Grooming, hygiene, dress, posture</p>
                    </div>
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Behavior:</span>
                      <p className="text-slate-600">Eye contact, motor activity, mannerisms</p>
                    </div>
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Speech:</span>
                      <p className="text-slate-600">Rate, volume, tone, coherence</p>
                    </div>
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Mood/Affect:</span>
                      <p className="text-slate-600">Subjective (mood) vs. objective (affect)</p>
                    </div>
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Thought Process:</span>
                      <p className="text-slate-600">Logical, circumstantial, tangential, loose</p>
                    </div>
                    <div className="p-2 bg-white/70 rounded">
                      <span className="font-bold text-purple-700">Thought Content:</span>
                      <p className="text-slate-600">Delusions, obsessions, suicidal/homicidal</p>
                    </div>
                  </div>
                </div>

                {/* Safety Assessment */}
                <div className="medical-card p-6 border-2 border-red-200" style={{backgroundColor: '#fef2f2'}}>
                  <h3 className="font-semibold text-red-700 mb-3 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    Safety Assessment
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-slate-700">Always assess for:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                      <li>Suicidal ideation (SI)</li>
                      <li>Homicidal ideation (HI)</li>
                      <li>Self-harm urges</li>
                      <li>Command hallucinations</li>
                      <li>Elopement risk</li>
                      <li>Access to lethal means</li>
                    </ul>
                    <div className="mt-3 p-2 bg-red-100 rounded">
                      <p className="text-xs text-red-700 font-medium">
                        Document safety assessments every shift and with any behavior change
                      </p>
                    </div>
                  </div>
                </div>

                {/* Therapeutic Communication Tips */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-3">Therapeutic Communication</h3>
                  <ul className="text-sm text-slate-600 space-y-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Use open-ended questions
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Active listening & reflection
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Validate feelings
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Set appropriate boundaries
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      Remain calm and non-judgmental
                    </li>
                  </ul>
                </div>

                {/* Condition-Specific Tips */}
                {matchedCondition && matchedCondition.behaviorAssessment && (
                  <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                    <h3 className="font-semibold text-primary mb-3">
                      Assessment Points for {matchedCondition.name}
                    </h3>
                    <ul className="text-sm space-y-2">
                      {matchedCondition.behaviorAssessment.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 p-2 bg-purple-50 rounded">
                          <Target className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* ASSESSMENT TAB */}
          {/* ============================================ */}
          <TabsContent value="assessment">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <ClipboardCheck className="w-6 h-6 text-primary" />
                    Physical Assessment
                  </h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {Object.entries(patient.physical_assessment).map(([system, finding]) => (
                      <div key={system} className="p-4 border border-slate-200 rounded-lg">
                        <h4 className="font-semibold text-primary capitalize mb-2">{system}</h4>
                        <p className="text-slate-700">{finding}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assessment Actions */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-4">Assessment Actions</h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Button 
                      variant="outline"
                      onClick={() => addScore('assessment', 'assess_pain', 15, 'Assessed pain level')}
                      disabled={scores.assessment.actions.includes('assess_pain')}
                    >
                      {scores.assessment.actions.includes('assess_pain') ? '✓ ' : ''}Assess Pain (0-10)
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => addScore('assessment', 'prioritize_concerns', 15, 'Prioritized concerns')}
                      disabled={scores.assessment.actions.includes('prioritize_concerns')}
                    >
                      {scores.assessment.actions.includes('prioritize_concerns') ? '✓ ' : ''}Prioritize Concerns
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleCommunicationAction('explain_procedures')}
                      disabled={scores.communication.actions.includes('explain_procedures')}
                    >
                      {scores.communication.actions.includes('explain_procedures') ? '✓ ' : ''}Explain Procedures
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => handleCommunicationAction('addressed_concerns')}
                      disabled={scores.communication.actions.includes('addressed_concerns')}
                    >
                      {scores.communication.actions.includes('addressed_concerns') ? '✓ ' : ''}Address Concerns
                    </Button>
                  </div>
                </div>

                {/* Auscultation Section */}
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-4 flex items-center gap-2">
                    <Volume2 className="w-5 h-5" />
                    Auscultation (NCLEX Practice)
                  </h3>
                  <p className="text-sm text-slate-600 mb-4">
                    Listen to this patient's lung, heart, and bowel sounds. Click play to hear, then review the clinical information.
                  </p>
                  
                  {patient && (() => {
                    const sounds = getPatientAuscultationSounds(patient, matchedCondition);
                    return (
                      <div className="space-y-4">
                        {/* Lung Sounds */}
                        <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/30">
                          <h4 className="text-sm font-bold text-blue-800 mb-2 flex items-center gap-2">
                            <Stethoscope className="w-4 h-4" /> Lung Sounds
                          </h4>
                          <AuscultationPlayer 
                            type="lungs" 
                            soundKey={sounds.lung}
                            showInfo={true}
                            size="default"
                          />
                          <Button 
                            variant="outline"
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => addScore('assessment', 'auscultate_lungs', 10, 'Auscultated lung sounds')}
                            disabled={scores.assessment.actions.includes('auscultate_lungs')}
                          >
                            {scores.assessment.actions.includes('auscultate_lungs') ? '✓ ' : ''}Mark as Assessed (+10 pts)
                          </Button>
                        </div>
                        
                        {/* Heart Sounds */}
                        <div className="border border-red-200 rounded-lg p-4 bg-red-50/30">
                          <h4 className="text-sm font-bold text-red-800 mb-2 flex items-center gap-2">
                            <Heart className="w-4 h-4" /> Heart Sounds
                          </h4>
                          <AuscultationPlayer 
                            type="heart" 
                            soundKey={sounds.heart}
                            showInfo={true}
                            size="default"
                          />
                          <Button 
                            variant="outline"
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => addScore('assessment', 'auscultate_heart', 10, 'Auscultated heart sounds')}
                            disabled={scores.assessment.actions.includes('auscultate_heart')}
                          >
                            {scores.assessment.actions.includes('auscultate_heart') ? '✓ ' : ''}Mark as Assessed (+10 pts)
                          </Button>
                        </div>
                        
                        {/* Bowel Sounds */}
                        <div className="border border-green-200 rounded-lg p-4 bg-green-50/30">
                          <h4 className="text-sm font-bold text-green-800 mb-2 flex items-center gap-2">
                            <Activity className="w-4 h-4" /> Bowel Sounds
                          </h4>
                          <AuscultationPlayer 
                            type="bowels" 
                            soundKey={sounds.bowel}
                            showInfo={true}
                            size="default"
                          />
                          <Button 
                            variant="outline"
                            size="sm"
                            className="mt-2 text-xs"
                            onClick={() => addScore('assessment', 'auscultate_bowels', 10, 'Auscultated bowel sounds')}
                            disabled={scores.assessment.actions.includes('auscultate_bowels')}
                          >
                            {scores.assessment.actions.includes('auscultate_bowels') ? '✓ ' : ''}Mark as Assessed (+10 pts)
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="font-semibold text-primary mb-4">Patient History</h3>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm font-medium text-slate-500">Medical History</p>
                      <ul className="mt-1 space-y-1">
                        {patient.medical_history.map((item, i) => (
                          <li key={i} className="text-sm text-slate-700">• {item}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-500">Diagnosis</p>
                      <p className="text-slate-700">{patient.diagnosis}</p>
                    </div>
                  </div>
                </div>

                {/* Assessment Score */}
                <div className="medical-card p-6 bg-gradient-to-br from-teal-50 to-cyan-50">
                  <h3 className="font-semibold mb-2">Assessment Score</h3>
                  <div className="text-center my-4">
                    <span className="text-4xl font-bold text-primary">{getCategoryScore('assessment')}%</span>
                  </div>
                  <Progress value={getCategoryScore('assessment')} className="h-3 mb-4" />
                  <div className="space-y-2">
                    {GRADING_CRITERIA.assessment.items.map(item => (
                      <div key={item.action} className="flex items-center gap-2 text-sm">
                        {scores.assessment.actions.includes(item.action) ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-slate-300" />
                        )}
                        <span className={scores.assessment.actions.includes(item.action) ? 'text-green-700' : 'text-slate-500'}>
                          {item.description}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* CARE PLAN TAB */}
          {/* ============================================ */}
          <TabsContent value="careplan">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h2 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <Heart className="w-6 h-6 text-primary" />
                    NANDA-I Care Plan
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Nursing Diagnoses */}
                    <div className="border-b border-slate-200 pb-4">
                      <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">1</span>
                        Nursing Diagnoses (NANDA-I)
                      </h3>
                      {carePlan.nursing_diagnoses.map((item, i) => (
                        <div key={i} className="mb-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-medium text-slate-500">Diagnosis #{i + 1}</span>
                            {carePlan.nursing_diagnoses.length > 1 && (
                              <button onClick={() => removeCarePlanItem('nursing_diagnoses', i)} className="text-red-500 hover:bg-red-50 rounded p-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <select
                            value={item.diagnosis}
                            onChange={(e) => updateCarePlanField('nursing_diagnoses', i, 'diagnosis', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2 bg-white"
                          >
                            <option value="">Select NANDA Diagnosis...</option>
                            {Object.entries(NANDA_DIAGNOSES).map(([category, diagnoses]) => (
                              <optgroup key={category} label={category}>
                                {diagnoses.map(d => (
                                  <option key={d} value={d}>{d}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={item.related_to}
                            onChange={(e) => updateCarePlanField('nursing_diagnoses', i, 'related_to', e.target.value)}
                            placeholder="Related to (R/T)..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2"
                          />
                          <input
                            type="text"
                            value={item.evidence}
                            onChange={(e) => updateCarePlanField('nursing_diagnoses', i, 'evidence', e.target.value)}
                            placeholder="As evidenced by (AEB)..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                          />
                        </div>
                      ))}
                      <button onClick={() => addCarePlanItem('nursing_diagnoses')} className="flex items-center gap-1 text-primary text-xs hover:underline">
                        <Plus className="w-3 h-3" /> Add Diagnosis
                      </button>
                    </div>

                    {/* Goals/Outcomes - NOC */}
                    <div className="border-b border-slate-200 pb-4">
                      <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">2</span>
                        Goals/Outcomes (NOC)
                      </h3>
                      {carePlan.goals.map((item, i) => (
                        <div key={i} className="mb-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-medium text-slate-500">Goal #{i + 1}</span>
                            {carePlan.goals.length > 1 && (
                              <button onClick={() => removeCarePlanItem('goals', i)} className="text-red-500 hover:bg-red-50 rounded p-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <select
                            value={item.outcome}
                            onChange={(e) => updateCarePlanField('goals', i, 'outcome', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2 bg-white"
                          >
                            <option value="">Select NOC Outcome...</option>
                            {Object.entries(NOC_OUTCOMES).map(([category, outcomes]) => (
                              <optgroup key={category} label={category}>
                                {outcomes.map(o => (
                                  <option key={o} value={o}>{o}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <select
                            value={item.timeframe}
                            onChange={(e) => updateCarePlanField('goals', i, 'timeframe', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2 bg-white"
                          >
                            <option value="">Select Timeframe...</option>
                            <option value="By end of shift">By end of shift</option>
                            <option value="Within 24 hours">Within 24 hours</option>
                            <option value="Within 48 hours">Within 48 hours</option>
                            <option value="By discharge">By discharge</option>
                          </select>
                          <input
                            type="text"
                            value={item.indicators}
                            onChange={(e) => updateCarePlanField('goals', i, 'indicators', e.target.value)}
                            placeholder="Measurable indicators..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                          />
                        </div>
                      ))}
                      <button onClick={() => addCarePlanItem('goals')} className="flex items-center gap-1 text-primary text-xs hover:underline">
                        <Plus className="w-3 h-3" /> Add Goal
                      </button>
                    </div>

                    {/* Interventions - NIC */}
                    <div className="border-b border-slate-200 pb-4">
                      <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">3</span>
                        Interventions (NIC)
                      </h3>
                      {carePlan.interventions.map((item, i) => (
                        <div key={i} className="mb-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-medium text-slate-500">Intervention #{i + 1}</span>
                            {carePlan.interventions.length > 1 && (
                              <button onClick={() => removeCarePlanItem('interventions', i)} className="text-red-500 hover:bg-red-50 rounded p-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <select
                            value={item.intervention}
                            onChange={(e) => updateCarePlanField('interventions', i, 'intervention', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2 bg-white"
                          >
                            <option value="">Select NIC Intervention...</option>
                            {Object.entries(NIC_INTERVENTIONS).map(([category, interventions]) => (
                              <optgroup key={category} label={category}>
                                {interventions.map(int => (
                                  <option key={int} value={int}>{int}</option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={item.rationale}
                            onChange={(e) => updateCarePlanField('interventions', i, 'rationale', e.target.value)}
                            placeholder="Rationale/Evidence..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2"
                          />
                          <select
                            value={item.frequency}
                            onChange={(e) => updateCarePlanField('interventions', i, 'frequency', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm bg-white"
                          >
                            <option value="">Select Frequency...</option>
                            <option value="Once">Once</option>
                            <option value="PRN">PRN (as needed)</option>
                            <option value="Q4H">Every 4 hours</option>
                            <option value="Daily">Daily</option>
                            <option value="Continuous">Continuous</option>
                          </select>
                        </div>
                      ))}
                      <button onClick={() => addCarePlanItem('interventions')} className="flex items-center gap-1 text-primary text-xs hover:underline">
                        <Plus className="w-3 h-3" /> Add Intervention
                      </button>
                    </div>

                    {/* Evaluations */}
                    <div className="pb-4">
                      <h3 className="font-semibold text-primary mb-3 flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">4</span>
                        Evaluation
                      </h3>
                      {carePlan.evaluations.map((item, i) => (
                        <div key={i} className="mb-3 p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-xs font-medium text-slate-500">Evaluation #{i + 1}</span>
                            {carePlan.evaluations.length > 1 && (
                              <button onClick={() => removeCarePlanItem('evaluations', i)} className="text-red-500 hover:bg-red-50 rounded p-1">
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            value={item.criteria}
                            onChange={(e) => updateCarePlanField('evaluations', i, 'criteria', e.target.value)}
                            placeholder="Evaluation criteria..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2"
                          />
                          <select
                            value={item.met}
                            onChange={(e) => updateCarePlanField('evaluations', i, 'met', e.target.value)}
                            className="w-full p-2 border border-slate-300 rounded-md text-sm mb-2 bg-white"
                          >
                            <option value="">Goal Status...</option>
                            <option value="Met">Goal Met</option>
                            <option value="Partially Met">Partially Met</option>
                            <option value="Not Met">Not Met</option>
                            <option value="Ongoing">Ongoing</option>
                          </select>
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateCarePlanField('evaluations', i, 'notes', e.target.value)}
                            placeholder="Notes/Reassessment plan..."
                            className="w-full p-2 border border-slate-300 rounded-md text-sm"
                          />
                        </div>
                      ))}
                      <button onClick={() => addCarePlanItem('evaluations')} className="flex items-center gap-1 text-primary text-xs hover:underline">
                        <Plus className="w-3 h-3" /> Add Evaluation
                      </button>
                    </div>

                    <Button 
                      onClick={saveCarePlan} 
                      disabled={savingCarePlan}
                      className="w-full bg-[#0F766E] hover:bg-[#0d6960]"
                    >
                      {savingCarePlan ? 'Saving...' : 'Save Care Plan'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                <div className="medical-card p-6 bg-gradient-to-br from-teal-50 to-cyan-50">
                  <h3 className="font-semibold mb-2">Documentation Score</h3>
                  <div className="text-center my-4">
                    <span className="text-4xl font-bold text-primary">{getCategoryScore('documentation')}%</span>
                  </div>
                  <Progress value={getCategoryScore('documentation')} className="h-3" />
                </div>
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* NURSING NOTE TAB - Comprehensive Documentation */}
          {/* ============================================ */}
          <TabsContent value="nursingnote">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Left Column - Patient Context (Pre-populated) */}
              <div className="xl:col-span-1 space-y-4">
                <div className="medical-card p-4 bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-200">
                  <h3 className="font-bold text-emerald-800 mb-3 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Patient Context
                  </h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-semibold">Name:</span> {patient?.patient_name}</p>
                    <p><span className="font-semibold">Age/Gender:</span> {patient?.age} y/o {patient?.gender}</p>
                    <p><span className="font-semibold">Diagnosis:</span> {patient?.diagnosis}</p>
                    <p><span className="font-semibold">Chief Complaint:</span> {patient?.chief_complaint || 'See diagnosis'}</p>
                  </div>
                </div>
                
                <div className="medical-card p-4 bg-white border border-slate-200">
                  <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-red-500" />
                    Current Vitals
                  </h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="bg-slate-50 p-2 rounded">
                      <span className="text-slate-500">BP:</span>
                      <span className="font-bold ml-1">{currentVitals?.bp}</span>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <span className="text-slate-500">HR:</span>
                      <span className="font-bold ml-1">{currentVitals?.heartRate} bpm</span>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <span className="text-slate-500">RR:</span>
                      <span className="font-bold ml-1">{currentVitals?.respiratoryRate}/min</span>
                    </div>
                    <div className="bg-slate-50 p-2 rounded">
                      <span className="text-slate-500">Temp:</span>
                      <span className="font-bold ml-1">{currentVitals?.temperature}°F</span>
                    </div>
                    <div className="bg-slate-50 p-2 rounded col-span-2">
                      <span className="text-slate-500">SpO2:</span>
                      <span className="font-bold ml-1">{currentVitals?.oxygenSaturation}%</span>
                    </div>
                  </div>
                </div>
                
                <div className="medical-card p-4 bg-amber-50 border border-amber-200">
                  <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5" />
                    NCLEX Tips
                  </h3>
                  <ul className="text-xs text-amber-700 space-y-1">
                    <li>• Use systematic head-to-toe approach</li>
                    <li>• Include NANDA-I nursing diagnoses</li>
                    <li>• Document evidence-based interventions</li>
                    <li>• Evaluate patient response to care</li>
                    <li>• Show clinical reasoning & prioritization</li>
                  </ul>
                </div>
              </div>
              
              {/* Right Column - Nursing Note Form */}
              <div className="xl:col-span-2 space-y-4">
                <div className="medical-card p-6 bg-white">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                      <FileText className="w-5 h-5" />
                      Comprehensive Nursing Note
                    </h3>
                    <span className="text-sm text-slate-500">
                      Sim Time: {formatSimulationTime(simulationTime)}
                    </span>
                  </div>
                  
                  <div className="space-y-4">
                    {/* Assessment Findings */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Assessment Findings (Head-to-Toe) <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={nursingNote.assessment_findings}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, assessment_findings: e.target.value }))}
                        placeholder="Document systematic assessment: General appearance, neurological (LOC, orientation, pupils), cardiovascular (heart sounds, pulses, edema), respiratory (breath sounds, work of breathing), GI (bowel sounds, abdomen), GU (output), skin (color, turgor, wounds), musculoskeletal (mobility, ROM)..."
                        className="min-h-[100px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Vital Signs Interpretation */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Vital Signs Interpretation
                      </label>
                      <Textarea
                        value={nursingNote.vital_signs_interpretation}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, vital_signs_interpretation: e.target.value }))}
                        placeholder="Interpret vital signs: What do they indicate? Are they within normal limits? Any concerning trends? How do they correlate with the patient's condition? What clinical significance do abnormalities have?"
                        className="min-h-[80px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Nursing Diagnoses */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Nursing Diagnoses (NANDA-I Format) <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={nursingNote.nursing_diagnoses}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, nursing_diagnoses: e.target.value }))}
                        placeholder="Use NANDA-I format: [Problem] related to [Etiology] as evidenced by [Signs/Symptoms]&#10;Example: Impaired gas exchange R/T alveolar-capillary membrane changes AEB SpO2 88%, dyspnea, use of accessory muscles&#10;List diagnoses in priority order..."
                        className="min-h-[100px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Interventions Performed */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Nursing Interventions Performed <span className="text-red-500">*</span>
                      </label>
                      <Textarea
                        value={nursingNote.interventions_performed}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, interventions_performed: e.target.value }))}
                        placeholder="Document all interventions with rationale:&#10;- Independent nursing interventions (positioning, monitoring, patient teaching)&#10;- Collaborative interventions (medications administered, treatments)&#10;- Safety interventions (fall precautions, aspiration precautions)&#10;Include time, dose, route for medications..."
                        className="min-h-[100px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Patient Response */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Patient Response to Interventions
                      </label>
                      <Textarea
                        value={nursingNote.patient_response}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, patient_response: e.target.value }))}
                        placeholder="Evaluate effectiveness of interventions:&#10;- Objective findings (vital signs changes, lab values, physical exam)&#10;- Subjective response (patient statements, pain level)&#10;- Were expected outcomes achieved? What modifications are needed?"
                        className="min-h-[80px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Clinical Reasoning */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Clinical Reasoning & Critical Thinking
                      </label>
                      <Textarea
                        value={nursingNote.clinical_reasoning}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, clinical_reasoning: e.target.value }))}
                        placeholder="Demonstrate critical thinking:&#10;- Why did you prioritize certain interventions?&#10;- What complications are you monitoring for?&#10;- What clinical changes would prompt immediate action?&#10;- How does this patient's presentation compare to expected findings for their diagnosis?"
                        className="min-h-[80px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Plan of Care */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Plan of Care (Next Steps)
                      </label>
                      <Textarea
                        value={nursingNote.plan_of_care}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, plan_of_care: e.target.value }))}
                        placeholder="Outline ongoing plan:&#10;- Continue monitoring (frequency, parameters)&#10;- Upcoming interventions or assessments&#10;- Discharge planning considerations&#10;- Interdisciplinary collaboration needs"
                        className="min-h-[60px] bg-slate-50"
                      />
                    </div>
                    
                    {/* Patient Education */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1">
                        Patient/Family Education
                      </label>
                      <Textarea
                        value={nursingNote.patient_education}
                        onChange={(e) => setNursingNote(prev => ({ ...prev, patient_education: e.target.value }))}
                        placeholder="Document teaching provided:&#10;- Topics covered (disease process, medications, warning signs)&#10;- Teaching methods used&#10;- Patient/family understanding (return demonstration, verbalization)&#10;- Barriers to learning identified"
                        className="min-h-[60px] bg-slate-50"
                      />
                    </div>
                    
                    <div className="flex gap-3 pt-4">
                      <Button 
                        onClick={submitNursingNote}
                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-3"
                        disabled={scoringNursingNote}
                      >
                        {scoringNursingNote ? (
                          <>
                            <Activity className="w-4 h-4 mr-2 animate-spin" />
                            Evaluating Against NCLEX Standards...
                          </>
                        ) : (
                          <>
                            <Award className="w-4 h-4 mr-2" />
                            Submit & Grade Nursing Note
                          </>
                        )}
                      </Button>
                      <Button 
                        onClick={clearNursingNote}
                        variant="outline"
                        className="px-6"
                        disabled={scoringNursingNote}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Clear
                      </Button>
                    </div>
                  </div>
                </div>
                
                {/* Scoring Results Panel */}
                {nursingNoteScore && (
                  <div className="medical-card p-6 bg-white border-2 border-emerald-300">
                    {/* Header with Score and NCLEX Result */}
                    <div className="flex items-center justify-between mb-4 pb-4 border-b">
                      <div>
                        <h3 className="text-lg font-bold text-emerald-800 flex items-center gap-2">
                          <Award className="w-5 h-5" />
                          NCLEX-Style Evaluation
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">{nursingNoteScore.timestamp}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className={`text-3xl font-bold px-4 py-2 rounded-lg ${
                          nursingNoteScore.grade === 'A' ? 'bg-green-100 text-green-700' :
                          nursingNoteScore.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                          nursingNoteScore.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                          nursingNoteScore.grade === 'D' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {nursingNoteScore.total_score}/100 ({nursingNoteScore.grade})
                        </div>
                        <div className={`text-xl font-bold px-4 py-2 rounded-lg ${
                          nursingNoteScore.nclex_result === 'PASS' ? 'bg-green-500 text-white' :
                          nursingNoteScore.nclex_result === 'BORDERLINE' ? 'bg-yellow-500 text-white' :
                          'bg-red-500 text-white'
                        }`}>
                          NCLEX: {nursingNoteScore.nclex_result}
                        </div>
                      </div>
                    </div>
                    
                    {/* Overall Feedback */}
                    <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-slate-700">{nursingNoteScore.overall_feedback}</p>
                      {nursingNoteScore.nclex_alignment && (
                        <p className="text-sm text-emerald-600 mt-2 italic">
                          <strong>NCLEX Alignment:</strong> {nursingNoteScore.nclex_alignment}
                        </p>
                      )}
                    </div>
                    
                    {/* Category Scores Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                      {nursingNoteScore.category_scores && Object.entries(nursingNoteScore.category_scores).map(([key, value]) => {
                        const maxScore = key === 'assessment' ? 25 : key === 'interventions' ? 20 : key === 'vital_signs' || key === 'nursing_diagnoses' ? 15 : key === 'patient_response' || key === 'clinical_reasoning' ? 10 : 5;
                        const percentage = Math.round((value.score / maxScore) * 100);
                        return (
                          <div key={key} className="p-3 bg-white border rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-slate-700 capitalize text-sm">
                                {key.replace(/_/g, ' ')}
                              </span>
                              <span className={`font-bold text-sm px-2 py-0.5 rounded ${
                                percentage >= 80 ? 'bg-green-100 text-green-700' :
                                percentage >= 60 ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }`}>
                                {value.score}/{maxScore}
                              </span>
                            </div>
                            <div className="w-full bg-slate-200 rounded-full h-2 mb-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  percentage >= 80 ? 'bg-green-500' :
                                  percentage >= 60 ? 'bg-yellow-500' :
                                  'bg-red-500'
                                }`}
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <p className="text-xs text-slate-600">{value.feedback}</p>
                            {value.nclex_tips && (
                              <p className="text-xs text-emerald-600 mt-1 italic">
                                <strong>NCLEX Tip:</strong> {value.nclex_tips}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Strengths and Improvements */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {nursingNoteScore.strengths && nursingNoteScore.strengths.length > 0 && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-bold text-green-700 text-sm mb-2 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" />
                            Strengths
                          </h4>
                          <ul className="text-sm text-green-600 space-y-1">
                            {nursingNoteScore.strengths.map((s, i) => (
                              <li key={i}>• {s}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {nursingNoteScore.areas_for_improvement && nursingNoteScore.areas_for_improvement.length > 0 && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <h4 className="font-bold text-amber-700 text-sm mb-2 flex items-center gap-1">
                            <AlertTriangle className="w-4 h-4" />
                            Areas for Improvement
                          </h4>
                          <ul className="text-sm text-amber-600 space-y-1">
                            {nursingNoteScore.areas_for_improvement.map((a, i) => (
                              <li key={i}>• {a}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    
                    {/* Priority Focus */}
                    {nursingNoteScore.priority_focus && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-bold text-blue-700 text-sm mb-1 flex items-center gap-1">
                          <Target className="w-4 h-4" />
                          Priority Focus for NCLEX Success
                        </h4>
                        <p className="text-sm text-blue-600">{nursingNoteScore.priority_focus}</p>
                      </div>
                    )}
                    
                    {/* Safety Concerns */}
                    {nursingNoteScore.safety_concerns && nursingNoteScore.safety_concerns.length > 0 && (
                      <div className="p-3 bg-red-50 rounded-lg border border-red-200 mt-3">
                        <h4 className="font-bold text-red-700 text-sm mb-1 flex items-center gap-1">
                          <AlertTriangle className="w-4 h-4" />
                          Safety Concerns Identified
                        </h4>
                        <ul className="text-sm text-red-600 space-y-1">
                          {nursingNoteScore.safety_concerns.map((c, i) => (
                            <li key={i}>• {c}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Note History */}
                {nursingNoteHistory.length > 0 && (
                  <div className="medical-card p-4 bg-slate-50">
                    <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Previous Nursing Notes ({nursingNoteHistory.length})
                    </h3>
                    <div className="space-y-2 max-h-[200px] overflow-y-auto">
                      {nursingNoteHistory.slice().reverse().map((entry) => (
                        <div key={entry.id} className="flex items-center justify-between p-2 bg-white rounded border">
                          <div>
                            <span className="text-sm text-slate-600">{entry.timestamp}</span>
                            <span className="text-xs text-slate-400 ml-2">Sim: {entry.simulation_time}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              entry.score.grade === 'A' ? 'bg-green-100 text-green-700' :
                              entry.score.grade === 'B' ? 'bg-blue-100 text-blue-700' :
                              entry.score.grade === 'C' ? 'bg-yellow-100 text-yellow-700' :
                              entry.score.grade === 'D' ? 'bg-orange-100 text-orange-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {entry.score.total_score}/100
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                              entry.score.nclex_result === 'PASS' ? 'bg-green-500 text-white' :
                              entry.score.nclex_result === 'BORDERLINE' ? 'bg-yellow-500 text-white' :
                              'bg-red-500 text-white'
                            }`}>
                              {entry.score.nclex_result}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* ============================================ */}
          {/* REPORT TAB - Detailed Feedback */}
          {/* ============================================ */}
          <TabsContent value="report">
            <div className="space-y-6">
              {/* Overall Score Card */}
              <div className="medical-card p-8 text-center" style={{backgroundColor: 'white'}}>
                <Award className={`w-16 h-16 mx-auto mb-4 ${overallScore >= 80 ? 'text-green-500' : overallScore >= 60 ? 'text-yellow-500' : 'text-red-500'}`} />
                <h2 className="text-3xl font-bold text-slate-800 mb-2">Overall Performance</h2>
                <p className="text-6xl font-bold mb-4" style={{ color: overallScore >= 80 ? '#22c55e' : overallScore >= 60 ? '#eab308' : '#ef4444' }}>
                  {overallScore}%
                </p>
                <p className="text-lg text-slate-600">
                  {overallScore >= 90 ? 'Excellent! You demonstrated exceptional clinical competency.' :
                   overallScore >= 80 ? 'Great job! You showed strong clinical skills.' :
                   overallScore >= 70 ? 'Good performance. Some areas need improvement.' :
                   overallScore >= 60 ? 'Satisfactory. Focus on the areas highlighted below.' :
                   'Needs improvement. Review the feedback and practice more.'}
                </p>
              </div>

              {/* Category Breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(GRADING_CRITERIA).map(([key, criteria]) => {
                  const score = getCategoryScore(key);
                  const Icon = criteria.icon;
                  return (
                    <div key={key} className="medical-card p-6" style={{backgroundColor: 'white'}}>
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-3 rounded-full ${score >= 80 ? 'bg-green-100' : score >= 60 ? 'bg-yellow-100' : 'bg-red-100'}`}>
                          <Icon className={`w-6 h-6 ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-slate-800">{criteria.name}</h3>
                          <p className={`text-2xl font-bold ${score >= 80 ? 'text-green-600' : score >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {score}%
                          </p>
                        </div>
                      </div>
                      <Progress value={score} className="h-2 mb-4" />
                      <div className="space-y-2">
                        {criteria.items.map(item => (
                          <div key={item.action} className="flex items-center gap-2 text-sm">
                            {scores[key].actions.includes(item.action) ? (
                              <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                            ) : (
                              <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                            )}
                            <span className={scores[key].actions.includes(item.action) ? 'text-slate-700' : 'text-slate-400'}>
                              {item.description} ({item.points} pts)
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Recommendations */}
              <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-primary" />
                  Areas for Improvement
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(GRADING_CRITERIA).map(([key, criteria]) => {
                    const score = getCategoryScore(key);
                    if (score >= 80) return null;
                    const missedItems = criteria.items.filter(item => !scores[key].actions.includes(item.action));
                    if (missedItems.length === 0) return null;
                    
                    return (
                      <div key={key} className="p-4 bg-red-50 border border-red-200 rounded-lg">
                        <h4 className="font-semibold text-red-800 mb-2">{criteria.name}</h4>
                        <p className="text-sm text-red-600 mb-2">Missing actions:</p>
                        <ul className="space-y-1">
                          {missedItems.map(item => (
                            <li key={item.action} className="text-sm text-red-700">• {item.description}</li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
                
                {overallScore >= 80 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg mt-4">
                    <p className="text-green-800 font-medium">🎉 Excellent work! You've demonstrated competency in all major areas.</p>
                  </div>
                )}
              </div>

              {/* Action Summary */}
              {responses.length > 0 && (
                <div className="medical-card p-6" style={{backgroundColor: 'white'}}>
                  <h3 className="text-xl font-bold text-slate-800 mb-4">Session Activity Summary</h3>
                  <p className="text-slate-600 mb-4">You performed {responses.length} patient interactions during this simulation.</p>
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {responses.map((item, index) => (
                      <div key={index} className="p-3 bg-slate-50 rounded text-sm">
                        <span className="font-medium text-slate-700">{item.action}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Return to Dashboard */}
              <div className="text-center">
                <Button onClick={() => navigate('/dashboard')} className="bg-[#0F766E] hover:bg-[#0d6960]">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default SimulationRoom;
